// DEPENDENCIES
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

// CSS
import "./ProjectViewer.css";

// MUI ICONS
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import BusinessIcon from "@mui/icons-material/Business";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import SettingsIcon from "@mui/icons-material/Settings";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// MUI COMPONENTS
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

// API
import { getAPICall, postAPICall, putAPICall, getBlobAPICall } from "../../../config/apiCalls";

// PDF export
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// CONSTANTS
import { notApplicable } from "../../Misc/Table/TanStackTableConstants";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// COMPONENTS
import DropboxFolderView from "./DropboxFolderView";
import ScheduleTab from "./ScheduleTab";
import StatusScheduleDialog from "./StatusScheduleDialog";
import DocumentViewerDialog from "./DocumentViewerDialog";
import ProjectConsoleEditAddDialog from "../../Admin/ProjectConsole/ProjectConsoleEditAddDialog/ProjectConsoleEditAddDialog";
import PhaseConsoleEditAddDialog from "../../Admin/PhaseConsole/PhaseConsoleEditAddDialog/PhaseConsoleEditAddDialog";
import PropertyConsoleEditAddDialog from "../../Admin/PropertyConsole/PropertyConsoleEditAddDialog/PropertyConsoleEditAddDialog";
import LinkToolsRecordDialog from "./LinkToolsRecordDialog";
import BudgetEditorTab, { buildResourceContext, budgetedServicesSpent } from "./BudgetEditorTab";
import { unbudgetedServices, priceUnbudgetedService, useUnbudgetedPricing } from "./budgetPricing";
import DesignTab from "./DesignTab";
import ProjectQuickAdd from "../../TimeEntry/ProjectQuickAdd";

const TABS = [
	{ id: "atAGlance", label: "At A Glance", sectionKey: "at_a_glance" },
	{ id: "amendments", label: "Amendments", sectionKey: "amendments" },
	{ id: "documents", label: "Documents", sectionKey: "documents", lazy: true },
	{ id: "budget", label: "Budget", sectionKey: "budgets" },
	{ id: "timeEntries", label: "Time Entries", sectionKey: "time_entries" },
	{ id: "schedule", label: "Schedule" },
	{ id: "invoices", label: "Invoices", sectionKey: "invoices" },
	{ id: "contacts", label: "Contacts", sectionKey: "contacts" },
	{ id: "design", label: "Design" },
	{ id: "dropbox", label: "Dropbox", sectionKey: "dropbox", lazy: true },
	{ id: "companyCam", label: "CompanyCam", sectionKey: "companycam", lazy: true },
];

const PROJECT_QUERY_PARAM = "recordId";
const TAB_QUERY_PARAM = "tab";
const DEFAULT_TAB_ID = "atAGlance";
const VALID_TAB_IDS = TABS.map((t) => t.id);
// Tabs amendment phases don't get: they have no CompanyCam project, Dropbox
// folder, HubSpot deal (contacts) or Tools phase record (design).
const AMENDMENT_HIDDEN_TAB_IDS = ["companyCam", "dropbox", "contacts", "design"];

// A stable reference (not a fresh `{}}` literal on every render) — both add dialogs'
// populate-on-open effects depend on this prop by reference, so a new object each
// render would re-fire that effect (and reset `loading`) on every unrelated
// ProjectViewer re-render, including mid-submit.
const EMPTY_RECORD_TO_EDIT = {};

const PHASE_REPORT_SECTION_CONFIG = [
	{ key: "at_a_glance", target: "at_a_glance" },
	{ key: "amendments", target: "amendments" },
	{ key: "documents", target: "documents", lazy: true },
	{ key: "contacts", target: "contacts" },
	{ key: "budgets", target: "budgets" },
	{ key: "time_entries", target: "time_entries" },
	{ key: "expense_entries", target: "expense_entries" },
	{ key: "invoices", target: "invoices" },
	{ key: "dropbox", target: "dropbox", lazy: true },
	{ key: "companycam", target: "companycam", lazy: true },
	{ key: "weekly_reports", target: "weekly_reports" },
];

// ─── URL BUILDERS ─────────────────────────────────────────────────────────────

const buildDropboxFolderUrl = (path) => {
	if (!path) return "https://www.dropbox.com/home";

	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	const encodedPath = normalizedPath
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");

	return `https://www.dropbox.com/home${encodedPath}`;
};

// ─── FORMATTERS ───────────────────────────────────────────────────────────────

const parseYMD = (s) => {
	if (!s) return null;
	const p = String(s).slice(0, 10).split("-");
	if (p.length !== 3) return null;
	const d = new Date(+p[0], +p[1] - 1, +p[2]);
	return isNaN(d.getTime()) ? null : d;
};

const formatNumber = (value) => {
	const num = parseFloat(value);
	if (isNaN(num)) return notApplicable;
	return new Intl.NumberFormat("en-US").format(num);
};

const formatCurrency = (value) => {
	const num = parseFloat(value);
	if (isNaN(num)) return notApplicable;
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(num);
};

const formatTimestamp = (value) => {
	if (!value) return notApplicable;
	const date = new Date(value);
	if (isNaN(date.getTime())) return notApplicable;
	return date.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
};

const formatDate = (value) => {
	if (!value) return notApplicable;
	const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
	if (isNaN(date.getTime())) return notApplicable;
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ─── INLINE UI HELPERS ────────────────────────────────────────────────────────

const renderInlineLoadingNotice = (message) => (
	<div className="projectViewerLoadingNotice">
		<div className="projectViewerLoadingNoticeIconWrap">
			<TimelapseIcon fontSize="inherit" />
		</div>
		<div className="projectViewerLoadingNoticeContent">
			<div className="projectViewerLoadingNoticeTitle">Loading</div>
			<div className="projectViewerLoadingNoticeMessage">{message}</div>
		</div>
		<div className="projectViewerLoadingNoticeBars">
			<div className="projectViewerLoadingNoticeBar projectViewerShimmer" />
			<div className="projectViewerLoadingNoticeBar projectViewerShimmer short" />
		</div>
	</div>
);

const renderSectionErrorPanel = (message) => (
	<div className="projectViewerEmptyStateMessage">{message}</div>
);

const renderMissingInfoNotice = (message) => (
	<div className="projectViewerMissingInfoNotice">
		<InfoOutlinedIcon fontSize="inherit" />
		<div>{message}</div>
	</div>
);

// ─── SECTION MERGE HELPER ─────────────────────────────────────────────────────

const mergePhaseReportSection = (currentModel, target, sectionData) => ({
	...currentModel,
	[target]: Array.isArray(sectionData)
		? sectionData
		: { ...(currentModel?.[target] || {}), ...sectionData },
});

// ─── INITIAL SECTION STATUS ───────────────────────────────────────────────────

const resetSectionStatus = () =>
	PHASE_REPORT_SECTION_CONFIG.reduce((acc, { key }) => {
		acc[key] = { loading: false, error: "" };
		return acc;
	}, {});

const SERVICE_RECOVERY_ROLES = ["client success", "client success administrator", "administrator"];

// ─── REACT COMPONENT ──────────────────────────────────────────────────────────

const ProjectViewer = (props) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const [searchParams, setSearchParams] = useSearchParams();

	// URL-driven state
	const bqeIdFromQuery = searchParams.get(PROJECT_QUERY_PARAM) || "";
	const tabFromQuery = searchParams.get(TAB_QUERY_PARAM) || "";
	// The tab the user picked; stays in the URL even while a phase that doesn't
	// have it is showing, so flipping on to a phase that does lands back on it.
	const preferredTab = VALID_TAB_IDS.includes(tabFromQuery)
		? tabFromQuery
		: DEFAULT_TAB_ID;

	// Browser (list) state
	const [phaseBrowserRows, setPhaseBrowserRows] = useState([]);
	const [isBrowserLoading, setIsBrowserLoading] = useState(false);
	const [browserError, setBrowserError] = useState("");

	// selectedBqeId drives the URL; selectedPhaseId is the NocoDB record ID used for API calls
	const [selectedBqeId, setSelectedBqeId] = useState(bqeIdFromQuery);
	const [selectedPhaseId, setSelectedPhaseId] = useState("");

	const selectedPhase =
		phaseBrowserRows.find((p) => p.bqeId === selectedBqeId) || null;

	// Amendment phases (flagged server-side: no Tools phase record and "amendment"
	// in the BQE name) get a reduced tab set. A preferred tab the phase doesn't
	// have falls back to At A Glance for this phase only.
	const isAmendmentPhase = !!selectedPhase?.isAmendmentPhase;
	const visibleTabs = isAmendmentPhase
		? TABS.filter((t) => !AMENDMENT_HIDDEN_TAB_IDS.includes(t.id))
		: TABS;
	const activeTab = visibleTabs.some((t) => t.id === preferredTab)
		? preferredTab
		: DEFAULT_TAB_ID;

	// Detail state
	const [phaseReportModel, setPhaseReportModel] = useState(null);
	const [isDetailLoading, setIsDetailLoading] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	// Bumped on every manual refresh so tabs that fetch their own data (outside
	// the phase report model) know to reload too.
	const [refreshCount, setRefreshCount] = useState(0);
	const [statusDialogOpen, setStatusDialogOpen] = useState(false);
	const [detailError, setDetailError] = useState("");
	const [sectionStatus, setSectionStatus] = useState(resetSectionStatus());
	const triggeredLazySectionsRef = useRef(new Set());
	const loggedPhaseRef = useRef("");
	// Bumped on every genuine phase switch (not on a same-phase refresh). Every async
	// callback below — the shell fetch, each eager section, and each lazy tab-section
	// fetch — captures the generation active when it started and checks it again after
	// every await, before touching phaseReportModel/sectionStatus. If the user has since
	// switched to a different phase, the response is discarded instead of being merged
	// into the new phase's view — otherwise a slow response for the old phase can land
	// after the new phase's data and silently overwrite it with stale numbers.
	const loadGenerationRef = useRef(0);

	// UI toggles
	const [searchTerm, setSearchTerm] = useState("");
	const [managerFilter, setManagerFilter] = useState("All Managers");
	const [stateFilter, setStateFilter] = useState("All Offices");
	const [statusFilter, setStatusFilter] = useState("All Statuses");
	const [browserPaneCollapsed, setBrowserPaneCollapsed] = useState(false);
	const [addMenuAnchor, setAddMenuAnchor] = useState(null);
	const [addProjectOpen, setAddProjectOpen] = useState(false);
	const [addPhaseOpen, setAddPhaseOpen] = useState(false);
	const [editProjectOpen, setEditProjectOpen] = useState(false);
	const [editPhaseOpen, setEditPhaseOpen] = useState(false);
	const [editMenuAnchor, setEditMenuAnchor] = useState(null);
	// "Link … to Tools" (a BQE record with no Tools record pointing at it) first asks
	// "create new or use existing?": linkChooser is { type, prefill } while that step
	// is open. Then either the Add dialog opens with that prefill (link*Prefill non-null
	// means it's open), or an existing Tools record opens in its Edit dialog with this
	// BQE record's id swapped in (relinkRecord is { type, record }). Kept in state, not
	// derived each render, so the dialogs' populate effects only run once.
	const [linkChooser, setLinkChooser] = useState(null);
	const [linkPropertyPrefill, setLinkPropertyPrefill] = useState(null);
	const [linkProjectPrefill, setLinkProjectPrefill] = useState(null);
	const [linkPhasePrefill, setLinkPhasePrefill] = useState(null);
	const [relinkRecord, setRelinkRecord] = useState(null);

	// Reference data for the Add Project / Add Phase dialogs (mirrors ProjectConsole /
	// PhaseConsole) — fetched lazily the first time the Add menu is opened rather than
	// on every Project Viewer visit, since most visits never touch these dialogs.
	const [addDialogDataLoaded, setAddDialogDataLoaded] = useState(false);
	const [addDialogDataLoading, setAddDialogDataLoading] = useState(false);
	const [addDialogDataFailed, setAddDialogDataFailed] = useState(false);
	const [addDialogProperties, setAddDialogProperties] = useState([]);
	const [addDialogEmployees, setAddDialogEmployees] = useState([]);
	const [addDialogProjects, setAddDialogProjects] = useState([]);
	const [addDialogBuildingComponents, setAddDialogBuildingComponents] = useState([]);
	const [addDialogPhaseTypes, setAddDialogPhaseTypes] = useState([]);
	const [addDialogPhaseNames, setAddDialogPhaseNames] = useState([]);
	const [addDialogPhases, setAddDialogPhases] = useState([]);
	const [phaseOverviewOpen, setPhaseOverviewOpen] = useState(false);
	const [invoiceListTab, setInvoiceListTab] = useState("outstanding");
	const [documentsSubTab, setDocumentsSubTab] = useState("proposals");
	const [viewingDocument, setViewingDocument] = useState(null);
	const [dropboxFolderTab, setDropboxFolderTab] = useState("phase");
	const [dropboxMetricsReady, setDropboxMetricsReady] = useState(false);
	const dropboxLoadingStartedRef = useRef(false);
	const [shellLoadedFor, setShellLoadedFor] = useState("");
	const [scheduleTimeOff, setScheduleTimeOff] = useState(new Map());
	const [scheduleHolidays, setScheduleHolidays] = useState([]);
	const [scheduleHolidaysLoaded, setScheduleHolidaysLoaded] = useState(false);
	const [dropboxFilterState, setDropboxFilterState] = useState({
		folderFilter: (() => { const s = localStorage.getItem("dropbox_tree_filter"); return s === "empty" || s === "not_empty" ? s : "all"; })(),
		fileVisibility: localStorage.getItem("dropbox_tree_file_visibility") === "show" ? "show" : "hide",
		typeFilters: { all: true, doc: false, xls: false, pdf: false, jpg: false, pst: false, mov: false },
		fileNameFilter: "",
		filtersOpen: false,
		viewMode: "all",
	});
	const [serviceRecoveryOpen, setServiceRecoveryOpen] = useState(false);
	const [contactsCommunicationTab, setContactsCommunicationTab] =
		useState("email");
	const [timeEntriesEmployeeFilter, setTimeEntriesEmployeeFilter] = useState("All");
	const [timeEntriesDateFrom, setTimeEntriesDateFrom] = useState("");
	const [timeEntriesDateTo, setTimeEntriesDateTo] = useState("");
	const [timeEntriesBillableFilter, setTimeEntriesBillableFilter] = useState("all");
	// Quick Add (time entry on this phase); timeSentRef: something reached BQE
	// since it opened, so the time sections reload when it closes
	const [quickAddOpen, setQuickAddOpen] = useState(false);
	const timeSentRef = useRef(false);
	const [emailCommSearch, setEmailCommSearch] = useState("");
	const [emailCommDateFrom, setEmailCommDateFrom] = useState("");
	const [emailCommDateTo, setEmailCommDateTo] = useState("");
	const [phoneCommSearch, setPhoneCommSearch] = useState("");
	const [phoneCommDateFrom, setPhoneCommDateFrom] = useState("");
	const [phoneCommDateTo, setPhoneCommDateTo] = useState("");
	const [commTimeRange, setCommTimeRange] = useState("all");
	const [expandedCallSummaries, setExpandedCallSummaries] = useState({});
	const toggleCallSummary = (key) => setExpandedCallSummaries((prev) => ({ ...prev, [key]: !prev[key] }));
	const [expandedEmailThreads, setExpandedEmailThreads] = useState({});
	const toggleEmailThread = (key) => setExpandedEmailThreads((prev) => ({ ...prev, [key]: !prev[key] }));
	const [listeningCallId, setListeningCallId] = useState(null);
	const [audioBlobUrl, setAudioBlobUrl] = useState(null);
	const [expandedWeeklyReports, setExpandedWeeklyReports] = useState({});
	const [downloadingReportId, setDownloadingReportId] = useState(null);
	const [reportDownloadError, setReportDownloadError] = useState(null);

	// Weekly report PDFs need a freshly signed URL from NocoDB — fetched only when the
	// user actually clicks Download, not for every report on every page load.
	const handleDownloadWeeklyReport = async (recordId) => {
		if (!recordId || downloadingReportId) return;
		// Open the tab synchronously (within the click handler) so browsers don't
		// treat the later async navigation as a blocked popup.
		const downloadWindow = window.open("", "_blank");
		setDownloadingReportId(recordId);
		setReportDownloadError(null);
		try {
			const res = await getAPICall(instance, accounts[0], "/api/phaseReports/weeklyReportDownloadUrl", { recordId });
			const url = res.data?.url;
			if (url && downloadWindow) {
				downloadWindow.location.href = url;
			} else {
				if (downloadWindow) downloadWindow.close();
				setReportDownloadError(recordId);
			}
		} catch (err) {
			if (downloadWindow) downloadWindow.close();
			setReportDownloadError(recordId);
		} finally {
			setDownloadingReportId(null);
		}
	};
	const [photosSortKey, setPhotosSortKey] = useState("photo_count");
	const [photosSortDir, setPhotosSortDir] = useState("desc");
	const [photosView, setPhotosView] = useState("all_time");
	const [canViewServiceRecovery, setCanViewServiceRecovery] = useState(false);

	// ── Fetch call recording blob when a call is selected for playback ───────
	useEffect(() => {
		if (!listeningCallId) {
			if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
			setAudioBlobUrl(null);
			return;
		}
		let cancelled = false;
		getBlobAPICall(instance, accounts[0], "/api/hubspot/callRecording", { engagementId: listeningCallId })
			.then((blob) => {
				if (cancelled) return;
				setAudioBlobUrl(URL.createObjectURL(blob));
			})
			.catch((err) => console.error("[callRecording] failed to load:", err?.message));
		return () => { cancelled = true; };
	}, [listeningCallId]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Fetch current user's roles to gate Service Recovery Notes ────────────
	useEffect(() => {
		if (!employeeID) return;
		getAPICall(instance, accounts[0], "/api/permissions/roles/byEmployee", { employeeID })
			.then((res) => {
				const roles = (res?.data || []).map((r) => String(r.name || "").toLowerCase());
				setCanViewServiceRecovery(roles.some((r) => SERVICE_RECOVERY_ROLES.includes(r)));
			})
			.catch(() => setCanViewServiceRecovery(false));
	}, [employeeID, instance, accounts]);

	// ── Fetch browser list on mount ──────────────────────────────────────────
	const fetchBrowserList = useCallback(async () => {
		setIsBrowserLoading(true);
		setBrowserError("");
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/phaseReports/list",
			);
			setPhaseBrowserRows(res.data || []);
		} catch (err) {
			setBrowserError(
				err?.response?.data?.message ||
					err?.message ||
					"Failed to load phase list.",
			);
		} finally {
			setIsBrowserLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => {
		fetchBrowserList();
		if (!bqeIdFromQuery) {
			postAPICall(instance, accounts[0], "/api/logs/access/add", {
				page_name: "Project Viewer",
				employeeID,
				notes: "Accessed the Project Viewer page",
			});
		}
	}, [instance, accounts]);

	// ── Sync selectedBqeId ↔ URL ────────────────────────────────────────────
	useEffect(() => {
		if (bqeIdFromQuery && bqeIdFromQuery !== selectedBqeId) {
			setSelectedBqeId(bqeIdFromQuery);
			return;
		}

		const nextParams = new URLSearchParams(searchParams);
		if (selectedBqeId) {
			nextParams.set(PROJECT_QUERY_PARAM, selectedBqeId);
		} else {
			nextParams.delete(PROJECT_QUERY_PARAM);
		}
		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [bqeIdFromQuery, selectedBqeId, searchParams, setSearchParams]);

	// ── Resolve BQE ID → NocoDB record ID once browser list is available ────
	useEffect(() => {
		if (!selectedBqeId) {
			setSelectedPhaseId("");
			return;
		}
		if (!phaseBrowserRows.length) return;
		const match = phaseBrowserRows.find((p) => p.bqeId === selectedBqeId);
		if (match && match.recordId !== selectedPhaseId) {
			setSelectedPhaseId(match.recordId);
		}
	}, [selectedBqeId, phaseBrowserRows]);

	// ── Load (or re-load) a phase's shell + sections ─────────────────────────
	// bust=false is a full load for a newly-selected phase (clears everything first).
	// bust=true is a manual refresh of an already-loaded phase: re-fetches eager
	// sections plus any lazy sections already triggered, bypassing the server cache,
	// without blanking out what's currently on screen while the fresh data arrives.
	const loadPhaseDetail = useCallback(async (recordId, { bust = false } = {}) => {
		// A same-phase refresh doesn't start a new generation — it should be superseded
		// the moment the user actually switches phases, but two overlapping refreshes of
		// the SAME phase aren't a data-integrity issue (isRefreshing already prevents
		// that anyway). A genuine phase switch always gets its own generation.
		const generation = bust ? loadGenerationRef.current : ++loadGenerationRef.current;
		const isStale = () => loadGenerationRef.current !== generation;

		if (bust) {
			setIsRefreshing(true);
		} else {
			triggeredLazySectionsRef.current.clear();
			setPhaseReportModel(null);
		}
		setIsDetailLoading(true);
		setDetailError("");
		setScheduleHolidaysLoaded(false);

		setSectionStatus((current) =>
			PHASE_REPORT_SECTION_CONFIG.reduce((acc, { key, lazy }) => {
				const refetching = !lazy || (bust && triggeredLazySectionsRef.current.has(key));
				acc[key] = refetching ? { loading: true, error: "" } : (bust ? current[key] : { loading: false, error: "" });
				return acc;
			}, {}),
		);

		try {
			const shellRes = await getAPICall(
				instance,
				accounts[0],
				"/api/phaseReports/shellByRecordId",
				{ recordId },
			);
			// The user has since selected a different phase — this response belongs to
			// a view that's no longer showing. Discard it rather than merging it in.
			if (isStale()) return;
			const shellModel = shellRes.data;
			setPhaseReportModel((current) => (bust ? { ...current, ...shellModel } : shellModel));
			setShellLoadedFor(recordId);
		} catch (err) {
			// Clearing isRefreshing has to happen even when stale — nothing else will
			// clear it for this refresh call, and leaving it wedged "true" would leave
			// the refresh icon spinning forever if the user switched phases mid-refresh.
			if (bust) setIsRefreshing(false);
			if (isStale()) return;
			const message =
				err?.response?.data?.message ||
				err?.message ||
				"Failed to load phase report.";
			setDetailError(message);
			setSectionStatus(resetSectionStatus());
			setIsDetailLoading(false);
			return;
		} finally {
			if (!isStale()) setIsDetailLoading(false);
		}

		if (isStale()) {
			if (bust) setIsRefreshing(false);
			return;
		}

		const sectionsToFetch = PHASE_REPORT_SECTION_CONFIG.filter(
			({ key, lazy }) => !lazy || (bust && triggeredLazySectionsRef.current.has(key)),
		);

		await Promise.allSettled(
			sectionsToFetch.map(async ({ key, target }) => {
				try {
					const sectionRes = await getAPICall(
						instance,
						accounts[0],
						"/api/phaseReports/sectionByRecordId",
						{ recordId, section: key, ...(bust ? { bust: 1 } : {}) },
					);
					if (isStale()) return;
					setPhaseReportModel((current) =>
						mergePhaseReportSection(current, target, sectionRes.data),
					);
					setSectionStatus((current) => ({
						...current,
						[key]: { loading: false, error: "" },
					}));
				} catch (err) {
					if (isStale()) return;
					const message =
						err?.response?.data?.message ||
						err?.message ||
						`Failed to load section: ${key}`;
					setSectionStatus((current) => ({
						...current,
						[key]: { loading: false, error: message },
					}));
				}
			}),
		);

		if (bust) setIsRefreshing(false);
	}, [instance, accounts]);

	// ── Fetch shell + sections when selectedPhaseId changes ─────────────────
	useEffect(() => {
		if (!selectedPhaseId) {
			setPhaseReportModel(null);
			setSectionStatus(resetSectionStatus());
			setDetailError("");
			return;
		}

		loadPhaseDetail(selectedPhaseId, { bust: false });
	}, [selectedPhaseId, loadPhaseDetail]);

	const handleRefreshPhase = useCallback(() => {
		if (!selectedPhaseId || isRefreshing) return;
		setRefreshCount((count) => count + 1);
		loadPhaseDetail(selectedPhaseId, { bust: true });
	}, [selectedPhaseId, isRefreshing, loadPhaseDetail]);

	// Re-reads the given sections past the server cache, in the background
	// (after time is sent from Quick Add)
	const reloadSections = useCallback(async (keys) => {
		if (!selectedPhaseId) return;
		const generationAtStart = loadGenerationRef.current;
		await Promise.allSettled(
			PHASE_REPORT_SECTION_CONFIG.filter(({ key }) => keys.includes(key)).map(async ({ key, target }) => {
				try {
					const sectionRes = await getAPICall(
						instance,
						accounts[0],
						"/api/phaseReports/sectionByRecordId",
						{ recordId: selectedPhaseId, section: key, bust: 1 },
					);
					if (loadGenerationRef.current !== generationAtStart) return;
					setPhaseReportModel((current) =>
						mergePhaseReportSection(current, target, sectionRes.data),
					);
				} catch (err) {
					console.error("ProjectViewer - reloadSections - error:", err);
				}
			}),
		);
	}, [selectedPhaseId, instance, accounts]);

	const closeQuickAdd = () => {
		setQuickAddOpen(false);
		if (timeSentRef.current) {
			timeSentRef.current = false;
			reloadSections(["time_entries", "budgets"]);
		}
	};

	// ── Lazy-load sections when the user first switches to their tab ─────────
	useEffect(() => {
		if (!selectedPhaseId) return;
		if (shellLoadedFor !== selectedPhaseId) return;
		const tab = TABS.find((t) => t.id === activeTab);
		if (!tab?.lazy || !tab.sectionKey) return;
		const sectionKey = tab.sectionKey;
		if (triggeredLazySectionsRef.current.has(sectionKey)) return;
		triggeredLazySectionsRef.current.add(sectionKey);

		setSectionStatus((current) => ({
			...current,
			[sectionKey]: { loading: true, error: "" },
		}));

		// Captured now, checked again after the await — if the user has since switched
		// to a different phase (loadPhaseDetail bumps this on every genuine switch),
		// this response is for a view that's no longer showing and must be discarded.
		const generationAtStart = loadGenerationRef.current;
		const isStale = () => loadGenerationRef.current !== generationAtStart;

		const fetchLazySection = async () => {
			const configEntry = PHASE_REPORT_SECTION_CONFIG.find((c) => c.key === sectionKey);
			try {
				const sectionRes = await getAPICall(
					instance,
					accounts[0],
					"/api/phaseReports/sectionByRecordId",
					{ recordId: selectedPhaseId, section: sectionKey },
				);
				if (isStale()) return;
				if (configEntry) {
					setPhaseReportModel((current) =>
						mergePhaseReportSection(current, configEntry.target, sectionRes.data),
					);
				}
				setSectionStatus((current) => ({
					...current,
					[sectionKey]: { loading: false, error: "" },
				}));
			} catch (err) {
				if (isStale()) return;
				const message =
					err?.response?.data?.message ||
					err?.message ||
					`Failed to load section: ${sectionKey}`;
				setSectionStatus((current) => ({
					...current,
					[sectionKey]: { loading: false, error: message },
				}));
			}
		};

		fetchLazySection();
	}, [activeTab, selectedPhaseId, shellLoadedFor, instance, accounts]);

	// ── Dropbox metrics ready state ───────────────────────────────────────────
	useEffect(() => {
		dropboxLoadingStartedRef.current = false;
		setDropboxMetricsReady(false);
	}, [selectedPhaseId]);

	// ── BambooHR time off for schedule ────────────────────────────────────────
	// Fires as soon as budgets + time_entries finish loading so the data is ready
	// before the user opens the Schedule tab.
	useEffect(() => {
		if (!selectedPhaseId) { setScheduleTimeOff(new Map()); setScheduleHolidays([]); setScheduleHolidaysLoaded(false); return; }
		if (sectionStatus["budgets"]?.loading || sectionStatus["time_entries"]?.loading) return;
		if (sectionStatus["budgets"]?.error || sectionStatus["time_entries"]?.error) {
			setScheduleTimeOff(new Map());
			setScheduleHolidays([]);
			setScheduleHolidaysLoaded(true);
			return;
		}

		const names = [...new Set([
			...(budgetsSection?.service_line_items || []).map((i) => i.resource).filter(Boolean),
			...(timeEntriesSection?.entries || []).map((e) => e.employee).filter(Boolean),
		])];
		if (!names.length) {
			setScheduleTimeOff(new Map());
			setScheduleHolidays([]);
			setScheduleHolidaysLoaded(true);
			return;
		}

		const durInfo = atAGlanceSection?.estimated_project_duration || {};
		const today = new Date();
		const projectStart = parseYMD(durInfo.start_date || phaseReportModel?.phase?.["CF-Start Date"]);
		const start = projectStart
			? new Date(projectStart.getFullYear() - 1, projectStart.getMonth(), projectStart.getDate()).toISOString().slice(0, 10)
			: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).toISOString().slice(0, 10);
		const end = projectStart && durInfo.duration_weeks
			? new Date(projectStart.getTime() + (durInfo.duration_weeks + 8) * 7 * 86400000).toISOString().slice(0, 10)
			: new Date(today.getFullYear() + 2, today.getMonth(), today.getDate()).toISOString().slice(0, 10);

		postAPICall(instance, accounts[0], "/api/bamboohr/schedule_time_off", { names, start, end })
			.then((res) => {
				const { timeOff = {}, holidays = [] } = res.data || {};
				const map = new Map();
				Object.entries(timeOff).forEach(([name, periods]) => {
					const parsed = periods
						.map((p) => ({ start: parseYMD(p.start), end: parseYMD(p.end) || parseYMD(p.start), dates: p.dates || {} }))
						.filter((p) => p.start && p.end);
					if (parsed.length) map.set(name, parsed);
				});
				setScheduleTimeOff(map);
				setScheduleHolidays(
					holidays
						.map((h) => ({ start: parseYMD(h.start), end: parseYMD(h.end || h.start), name: h.name }))
						.filter((h) => h.start && h.end)
				);
				setScheduleHolidaysLoaded(true);
			})
			.catch(() => { setScheduleTimeOff(new Map()); setScheduleHolidaysLoaded(true); });
	}, [selectedPhaseId, sectionStatus["budgets"]?.loading, sectionStatus["time_entries"]?.loading]);

	const dropboxSectionLoading = !!sectionStatus["dropbox"]?.loading;
	useEffect(() => {
		if (dropboxSectionLoading) {
			dropboxLoadingStartedRef.current = true;
		} else if (dropboxLoadingStartedRef.current) {
			setDropboxMetricsReady(true);
		}
	}, [dropboxSectionLoading]);

	// ── Log project view when a phase is selected ────────────────────────────
	useEffect(() => {
		if (!selectedBqeId || !selectedPhase || loggedPhaseRef.current === selectedBqeId) return;
		loggedPhaseRef.current = selectedBqeId;
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Project Viewer",
			employeeID,
			notes: `Viewed project: ${selectedPhase.displayName || selectedBqeId}`,
		});
	}, [selectedBqeId, selectedPhase, instance, accounts, employeeID]);

	// ── Derived: records from phaseReportModel ─────────────────────────────
	const selectedPhaseRecord = phaseReportModel?.phase || null;
	const selectedProjectRecord = phaseReportModel?.project || null;
	const selectedPropertyRecord = phaseReportModel?.properties?.[0] || null;

	// The Tools DB `projects` row behind the currently viewed BQE project. `projects.bqe_id`
	// stores the real BQE project id (from BQE's own API), not NocoDB's internal row id —
	// so this has to match against phaseReportModel.project["BQE Id"], not its `.id`.
	// Admin editing needs this local row (id, property_id, hubspot_id, etc.); the
	// BQE record above doesn't carry those Tools-only fields.
	const editableProjectRecord =
		addDialogProjects.find(
			(project) =>
				selectedProjectRecord?.["BQE Id"] != null &&
				String(project.bqe_id) === String(selectedProjectRecord["BQE Id"])
		) || null;

	// Same idea for the Tools DB `properties` row behind the BQE property.
	const editablePropertyRecord =
		addDialogProperties.find(
			(property) =>
				selectedPropertyRecord?.["BQE Id"] != null &&
				String(property.bqe_id) === String(selectedPropertyRecord["BQE Id"])
		) || null;

	// Same idea for the Tools DB `phases` row: `phases.bqe_id` is the BQE phase id.
	const editablePhaseRecord =
		addDialogPhases.find(
			(phase) =>
				selectedPhaseRecord?.["BQE Id"] != null &&
				String(phase.bqe_id) === String(selectedPhaseRecord["BQE Id"])
		) || null;

	const selectedPropertyName =
		selectedPhase?.propertyName ||
		selectedPropertyRecord?.["BQE Display Name"] ||
		selectedPropertyRecord?.["BQE Name"] ||
		notApplicable;

	const selectedProjectName =
		selectedPhase?.projectName ||
		selectedProjectRecord?.["BQE Name"] ||
		selectedProjectRecord?.["BQE Display Name"] ||
		notApplicable;

	const selectedPhaseName =
		selectedPhase?.phaseName ||
		selectedPhaseRecord?.["BQE Name"] ||
		notApplicable;

	const selectedPhaseEyebrow =
		selectedPhase?.displayName ||
		selectedPhaseRecord?.["BQE Display Name"] ||
		notApplicable;

	const selectedStartedDate = formatDate(
		selectedPhaseRecord?.["CF-Start Date"],
	);

	// ── Section load state helpers ───────────────────────────────────────────
	const atAGlanceLoaded = phaseReportModel !== null && !sectionStatus["at_a_glance"]?.loading && !sectionStatus["at_a_glance"]?.error;

	// ── Section shortcuts ─────────────────────────────────────────────────────
	const atAGlanceSection = phaseReportModel?.at_a_glance || {};
	const amendmentsSection = phaseReportModel?.amendments || {};
	const documentsSection = phaseReportModel?.documents || {};
	const contactsSection = phaseReportModel?.contacts || {};
	const budgetsSection = phaseReportModel?.budgets || {};
	const timeEntriesSection = phaseReportModel?.time_entries || {};
	const expenseEntriesSection = phaseReportModel?.expense_entries || {};
	const invoicesSection = phaseReportModel?.invoices || {};
	const dropboxSection = phaseReportModel?.dropbox || {};
	const companyCamSection = phaseReportModel?.companycam || {};
	const weeklyReports = phaseReportModel?.weekly_reports || [];

	// ── Budget $ summary (At A Glance) ────────────────────────────────────────
	// Contract vs. budgeted vs. actually spent, from the cached budget lines and
	// synced time/expense entries. Same definitions as the Budget tab: hours not
	// in the budget are priced from the budget's fee schedule (or the default
	// sheet when there's no budget), which is only fetched when there are any.
	const budgetMoneyLoading = !!(sectionStatus["budgets"]?.loading || sectionStatus["time_entries"]?.loading || sectionStatus["expense_entries"]?.loading);
	const budgetServiceItems = budgetsSection.service_line_items || [];
	const budgetExpenseItems = budgetsSection.expense_line_items || [];
	const loggedExpenseEntries = expenseEntriesSection.entries || [];
	const budgetResourceContext = buildResourceContext(budgetServiceItems, budgetExpenseItems, timeEntriesSection.entries || [], loggedExpenseEntries);
	const budgetServiceRows = budgetServiceItems.map((i) => ({ resourceKey: budgetResourceContext.keyFor(i.resource, i.resource_id), rate: i.billRate }));
	const unbudgetedServiceHours = unbudgetedServices(budgetServiceItems, timeEntriesSection.entries || [], budgetResourceContext, new Set(budgetServiceRows.map((r) => r.resourceKey)));
	const unbudgetedPricing = useUnbudgetedPricing(instance, accounts[0], budgetsSection.bqe_budget_id || null, !budgetMoneyLoading && unbudgetedServiceHours.length > 0, selectedPhaseName);
	const budgetMoney = (() => {
		const budgetedTotal = budgetServiceItems.reduce((s, i) => s + (Number(i.hours) || 0) * (Number(i.billRate) || 0), 0)
			+ budgetExpenseItems.reduce((s, i) => s + (Number(i.charge_amount) || 0), 0);
		const priced = unbudgetedServiceHours.map((u) => priceUnbudgetedService(u, { ...unbudgetedPricing, serviceLineItems: budgetServiceItems, keyFor: budgetResourceContext.keyFor }));
		const spentServices = budgetedServicesSpent(budgetServiceRows, budgetResourceContext)
			+ priced.reduce((s, u) => s + (u.spentAmount || 0), 0);
		const spentExpenses = loggedExpenseEntries.reduce((s, e) => s + (Number(e.charge_amount) || 0), 0);
		const unpricedHours = priced.reduce((s, u) => s + (u.unpricedHours || 0), 0);
		return { hasBudget: budgetServiceItems.length + budgetExpenseItems.length > 0, budgetedTotal, spentServices, spentExpenses, spentTotal: spentServices + spentExpenses, unpricedHours };
	})();

	// ── Project Status Update — free-text note stored on the HubSpot deal ────────
	// Shown read-only by default, edited in place via the "Edit" button, and
	// written straight back to HubSpot on save. Mirrors the PM Project Dashboard's
	// Project Portfolio implementation of the same field.
	const projectStatusDealId = contactsSection?.hubspot_deal_id || null;
	const [statusText, setStatusText] = useState("");
	const [statusDraft, setStatusDraft] = useState("");
	const [isEditingStatus, setIsEditingStatus] = useState(false);
	const [statusLoaded, setStatusLoaded] = useState(false);
	const [statusLoading, setStatusLoading] = useState(false);
	const [statusSaving, setStatusSaving] = useState(false);
	const [statusError, setStatusError] = useState("");
	const [statusSavedAt, setStatusSavedAt] = useState(0);
	const statusSavedTimeoutRef = useRef(null);

	// Reset when the viewed phase changes so state from the previous phase doesn't leak.
	useEffect(() => {
		setStatusText("");
		setStatusDraft("");
		setIsEditingStatus(false);
		setStatusLoaded(false);
		setStatusLoading(false);
		setStatusError("");
		setStatusSavedAt(0);
	}, [selectedPhaseId]);

	useEffect(() => () => clearTimeout(statusSavedTimeoutRef.current), []);

	useEffect(() => {
		if (!projectStatusDealId || statusLoaded) return;
		let cancelled = false;
		setStatusLoading(true);
		setStatusError("");
		getAPICall(instance, accounts[0], "/api/hubspot/projectStatusUpdate", { dealID: projectStatusDealId })
			.then((res) => { if (!cancelled) setStatusText(res.data?.value || ""); })
			.catch(() => { if (!cancelled) setStatusError("Failed to load status update."); })
			.finally(() => { if (!cancelled) { setStatusLoading(false); setStatusLoaded(true); } });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectStatusDealId, statusLoaded]);

	const startEditingProjectStatus = () => {
		setStatusDraft(statusText);
		setStatusError("");
		setIsEditingStatus(true);
	};

	const cancelEditingProjectStatus = () => {
		setIsEditingStatus(false);
		setStatusError("");
	};

	const saveProjectStatusUpdate = () => {
		if (!projectStatusDealId || statusSaving) return;
		setStatusSaving(true);
		setStatusError("");
		postAPICall(instance, accounts[0], "/api/hubspot/projectStatusUpdate", { dealID: projectStatusDealId, value: statusDraft })
			.then(() => {
				setStatusText(statusDraft);
				setIsEditingStatus(false);
				setStatusSavedAt(Date.now());
				clearTimeout(statusSavedTimeoutRef.current);
				statusSavedTimeoutRef.current = setTimeout(() => setStatusSavedAt(0), 2500);
			})
			.catch(() => setStatusError("Failed to save status update."))
			.finally(() => setStatusSaving(false));
	};

	// ── Time entry filter options ─────────────────────────────────────────────
	const timeEntryEmployees = useMemo(() => {
		const entries = timeEntriesSection?.entries || [];
		return [...new Set(entries.map((e) => e.employee).filter(Boolean))].sort();
	}, [timeEntriesSection]);

	const filteredTimeEntries = useMemo(() => {
		const entries = timeEntriesSection?.entries || [];
		const from = timeEntriesDateFrom ? new Date(timeEntriesDateFrom) : null;
		const to = timeEntriesDateTo ? new Date(timeEntriesDateTo) : null;
		if (to) to.setUTCHours(23, 59, 59, 999);
		return entries
			.filter((entry) => {
				if (timeEntriesEmployeeFilter !== "All" && entry.employee !== timeEntriesEmployeeFilter) return false;
				if (timeEntriesBillableFilter === "billable" && !entry.billable) return false;
				if (timeEntriesBillableFilter === "nonBillable" && entry.billable) return false;
				if (from || to) {
					const d = new Date(entry.date);
					if (isNaN(d.getTime())) return false;
					if (from && d < from) return false;
					if (to && d > to) return false;
				}
				return true;
			})
			.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
	}, [timeEntriesSection, timeEntriesEmployeeFilter, timeEntriesBillableFilter, timeEntriesDateFrom, timeEntriesDateTo]);

	// Exports the currently filtered time entry table, so the PDF matches what's on screen.
	const handleExportTimeEntriesPdf = () => {
		// formatDate/formatNumber return the styled notApplicable element on bad input; the PDF needs plain text
		const pdfText = (value) => (value === notApplicable ? "N/A" : value);
		const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
		const title = [selectedPropertyName, selectedProjectName, selectedPhaseName]
			.filter((part) => part && part !== notApplicable)
			.join(": ") || "Time Entries";
		const filterParts = [
			timeEntriesEmployeeFilter !== "All" ? `Employee: ${timeEntriesEmployeeFilter}` : null,
			timeEntriesBillableFilter === "billable" ? "Billable only" : timeEntriesBillableFilter === "nonBillable" ? "Non-billable only" : null,
			timeEntriesDateFrom ? `From ${pdfText(formatDate(timeEntriesDateFrom))}` : null,
			timeEntriesDateTo ? `To ${pdfText(formatDate(timeEntriesDateTo))}` : null,
		].filter(Boolean);
		const totalHours = filteredTimeEntries.reduce((sum, entry) => sum + (Number(entry.hours) || 0), 0);

		doc.setFontSize(14);
		doc.text(title, 40, 40);
		doc.setFontSize(9);
		doc.setTextColor(100);
		doc.text(
			`Time Entries · Exported ${new Date().toLocaleDateString()}${filterParts.length ? ` · ${filterParts.join(" · ")}` : ""}`,
			40,
			56
		);

		autoTable(doc, {
			startY: 70,
			head: [["Date", "Employee", "Hours", "Billable", "Memo"]],
			body: filteredTimeEntries.map((entry) => [
				pdfText(formatDate(entry.date)),
				entry.employee || "",
				entry.hours ?? "",
				entry.billable ? "Yes" : "No",
				entry.memo || "",
			]),
			foot: [["Total", "", pdfText(formatNumber(totalHours)), "", `${filteredTimeEntries.length} entries`]],
			showFoot: "lastPage",
			styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
			headStyles: { fillColor: [55, 65, 81] },
			footStyles: { fillColor: [243, 244, 246], textColor: 20 },
			columnStyles: {
				0: { cellWidth: 65 },
				1: { cellWidth: 100 },
				2: { cellWidth: 40, halign: "right" },
				3: { cellWidth: 45 },
			},
			margin: { left: 40, right: 40 },
		});

		const fileName = `${title} - Time Entries`.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();
		doc.save(`${fileName}.pdf`);
	};

	const commEffectiveDateFrom = commTimeRange === "last_week"
		? (atAGlanceSection?.reporting_window?.period_start || "")
		: null;
	const commEffectiveDateTo = commTimeRange === "last_week"
		? (atAGlanceSection?.reporting_window?.period_end || "")
		: null;

	const filteredEmailThreads = useMemo(() => {
		const threads = contactsSection?.email_threads_last_week || [];
		const term = emailCommSearch.trim().toLowerCase();
		const rawFrom = commEffectiveDateFrom ?? emailCommDateFrom;
		const rawTo = commEffectiveDateTo ?? emailCommDateTo;
		const from = rawFrom ? new Date(rawFrom) : null;
		const to = rawTo ? new Date(rawTo) : null;
		if (to) to.setUTCHours(23, 59, 59, 999);
		return threads.filter((t) => {
			if (term && !(
				t.sender?.toLowerCase().includes(term) ||
				(t.recipients || []).some((r) => r.toLowerCase().includes(term)) ||
				t.subject?.toLowerCase().includes(term)
			)) return false;
			if (from || to) {
				const d = t.last_sent ? new Date(t.last_sent) : null;
				if (!d || isNaN(d.getTime())) return false;
				if (from && d < from) return false;
				if (to && d > to) return false;
			}
			return true;
		});
	}, [contactsSection?.email_threads_last_week, emailCommSearch, emailCommDateFrom, emailCommDateTo, commEffectiveDateFrom, commEffectiveDateTo]);

	const filteredPhoneCalls = useMemo(() => {
		const calls = contactsSection?.phone_communication_last_week || [];
		const term = phoneCommSearch.trim().toLowerCase();
		const rawFrom = commEffectiveDateFrom ?? phoneCommDateFrom;
		const rawTo = commEffectiveDateTo ?? phoneCommDateTo;
		const from = rawFrom ? new Date(rawFrom) : null;
		const to = rawTo ? new Date(rawTo) : null;
		if (to) to.setUTCHours(23, 59, 59, 999);
		return calls.filter((c) => {
			if (term && !(
				c.contact?.toLowerCase().includes(term) ||
				c.j2_user?.toLowerCase().includes(term) ||
				c.title?.toLowerCase().includes(term) ||
				c.status?.toLowerCase().includes(term) ||
				c.body?.toLowerCase().includes(term) ||
				c.summary?.toLowerCase().includes(term)
			)) return false;
			if (from || to) {
				const d = c.date ? new Date(c.date) : null;
				if (!d || isNaN(d.getTime())) return false;
				if (from && d < from) return false;
				if (to && d > to) return false;
			}
			return true;
		});
	}, [contactsSection?.phone_communication_last_week, phoneCommSearch, phoneCommDateFrom, phoneCommDateTo, commEffectiveDateFrom, commEffectiveDateTo]);

	// ── CompanyCam sorted photos ──────────────────────────────────────────────
	const sortedPhotos = useMemo(() => {
		const rows = photosView === "all_time"
			? companyCamSection?.all_time_photos_by_employee || []
			: companyCamSection?.weekly_photos_by_employee || [];
		return [...rows].sort((a, b) => {
			const av = photosSortKey === "photo_count" ? a.photo_count : a.employee_name;
			const bv = photosSortKey === "photo_count" ? b.photo_count : b.employee_name;
			if (av < bv) return photosSortDir === "asc" ? -1 : 1;
			if (av > bv) return photosSortDir === "asc" ? 1 : -1;
			return 0;
		});
	}, [companyCamSection, photosSortKey, photosSortDir, photosView]);

	const handlePhotosSort = (key) => {
		if (key === photosSortKey) {
			setPhotosSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setPhotosSortKey(key);
			setPhotosSortDir(key === "photo_count" ? "desc" : "asc");
		}
	};

	// ── Derived from atAGlance ────────────────────────────────────────────────
	const quickLinks = atAGlanceSection?.quick_links || [];
	const hubspotQuickLink = quickLinks.find((l) => l.key === "hubspot");
	const companyCamQuickLink = quickLinks.find((l) => l.key === "companycam");
	const bqeQuickLink = quickLinks.find((l) => l.key === "bqe");
	const hubspotDealUrl = hubspotQuickLink?.href || "";
	const companyCamUrl = companyCamQuickLink?.href || "";
	const bqeUrl = bqeQuickLink?.href || "";
	const bqeBudgetUrl = bqeUrl
		? bqeUrl.replace(/([?&]tab=)[^&]*/, "$1budget")
		: "";
	const bqeTransactionsUrl = bqeUrl
		? bqeUrl.replace(/([?&]tab=)[^&]*/, "$1transactions")
		: "";
	const dropboxPath = selectedPhase?.dropboxPath || dropboxSection?.dropbox_path || "";
	const dropboxGeneralPath = dropboxPath
		? dropboxPath.replace(/\/[^/]+$/, "") + "/1. General"
		: "";
	const dropboxFolderUrl = buildDropboxFolderUrl(dropboxPath);

	// Mirrors PropertyViewer's hero quick-link badges, to the left of the nav arrows.
	const heroQuickLinkItems = [
		{ key: "hubspot", label: "HubSpot", short: "HS", url: hubspotDealUrl, badgeClass: "hubspot" },
		{ key: "companycam", label: "CompanyCam", short: "CC", url: companyCamUrl, badgeClass: "ccam" },
		{ key: "bqe", label: "BQE Core", short: "BQ", url: bqeUrl, badgeClass: "bqe" },
		{ key: "dropbox", label: "Dropbox", short: "DB", url: dropboxPath ? dropboxFolderUrl : "", badgeClass: "dropbox" },
	].filter((item) => !(isAmendmentPhase && (item.key === "companycam" || item.key === "dropbox")));

	const renderHeroQuickLinkIcons = () => (
		<div className="projectViewerHeroQuickLinks">
			{heroQuickLinkItems.map((item) => (
				<a
					key={item.key}
					href={item.url || undefined}
					target={item.url ? "_blank" : undefined}
					rel={item.url ? "noopener noreferrer" : undefined}
					className={`projectViewerIntegrationBadge projectViewerHeroLinkBadge ${item.badgeClass}${!item.url ? " disabled" : ""}`}
					title={item.url ? item.label : "Unavailable"}
					aria-label={item.label}
				>
					{item.short}
				</a>
			))}
		</div>
	);

	const reportingPeriodDisplay =
		atAGlanceSection?.reporting_window?.period_display || "";
	const generatedOnDisplay =
		atAGlanceSection?.reporting_window?.generated_on_display || "";

	const budgetIndicator = atAGlanceSection?.budget_indicator || {};
	const schedDur = atAGlanceSection?.estimated_project_duration || {};

	// ── Loading progress ──────────────────────────────────────────────────────
	const eagerSections = PHASE_REPORT_SECTION_CONFIG.filter((c) => !c.lazy);
	const loadedEagerCount = eagerSections.filter((c) => !sectionStatus[c.key]?.loading).length;
	const loadProgress = eagerSections.length > 0 ? (loadedEagerCount / eagerSections.length) * 100 : 100;
	const allEagerLoaded = loadedEagerCount === eagerSections.length;

	// ── Browser list helpers ──────────────────────────────────────────────────
	const statusOptions = ["All Statuses", "Active", "On Hold"];

	const managerOptions = useMemo(
		() => [
			"All Managers",
			...Array.from(new Set(phaseBrowserRows.map((p) => p.owner))).filter(Boolean).sort(),
		],
		[phaseBrowserRows],
	);

	const stateOptions = useMemo(
		() => [
			"All Offices",
			...Array.from(new Set(phaseBrowserRows.map((p) => p.location))).filter(Boolean).sort(),
		],
		[phaseBrowserRows],
	);

	// Search, Manager, Office, and Status all behave the same way: they're a "find", not a
	// hard exclusion. If any phase in a project matches every active filter, the whole
	// project's phases stay visible so the group doesn't show a truncated project.
	const filteredPhases = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();

		const phaseMatches = (phase) => {
			const matchesSearch =
				!term ||
				[
					phase.propertyName,
					phase.projectName,
					phase.displayName,
					phase.owner,
					phase.client,
					phase.status,
					phase.location,
				]
					.join(" ")
					.toLowerCase()
					.includes(term);

			const matchesManager =
				managerFilter === "All Managers" || phase.owner === managerFilter;

			const matchesState =
				stateFilter === "All Offices" || phase.location === stateFilter;

			const matchesStatus =
				statusFilter === "All Statuses" ||
				String(phase.status || "").trim().toLowerCase() ===
					statusFilter.toLowerCase();

			return matchesSearch && matchesManager && matchesState && matchesStatus;
		};

		const matchingGroupKeys = new Set(
			phaseBrowserRows
				.filter(phaseMatches)
				.map((phase) => phase.projectId || `standalone-${phase.recordId}`),
		);

		return phaseBrowserRows
			.filter((phase) => matchingGroupKeys.has(phase.projectId || `standalone-${phase.recordId}`))
			.sort((a, b) => {
				const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
				const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
				if (bTime !== aTime) return bTime - aTime;
				return Number(b.recordId) - Number(a.recordId);
			});
	}, [phaseBrowserRows, managerFilter, stateFilter, statusFilter, searchTerm]);

	const currentFilteredIndex = filteredPhases.findIndex((p) => p.bqeId === selectedBqeId);
	const prevPhase = currentFilteredIndex > 0 ? filteredPhases[currentFilteredIndex - 1] : null;
	const nextPhase = currentFilteredIndex !== -1 && currentFilteredIndex < filteredPhases.length - 1
		? filteredPhases[currentFilteredIndex + 1]
		: null;

	// Groups the browser list by parent project (BQE Parent Id) so phases from the
	// same project render together under one header, in the same order filteredPhases
	// already established. Phases with no projectId (orphaned records) get their own
	// standalone group keyed by recordId.
	const groupedPhases = useMemo(() => {
		const groups = new Map();
		filteredPhases.forEach((phase) => {
			const groupKey = phase.projectId || `standalone-${phase.recordId}`;
			if (!groups.has(groupKey)) {
				groups.set(groupKey, {
					key: groupKey,
					propertyName: phase.propertyName,
					projectName: phase.projectName,
					phases: [],
				});
			}
			groups.get(groupKey).phases.push(phase);
		});
		return Array.from(groups.values());
	}, [filteredPhases]);

	// ── Invoices derived ──────────────────────────────────────────────────────
	const unpaidInvoices = useMemo(
		() =>
			(invoicesSection.invoices || []).filter(
				(inv) => inv.paid_status === "unpaid" && !inv.is_draft && !inv.is_void,
			),
		[invoicesSection.invoices],
	);

	const draftInvoices = useMemo(
		() => (invoicesSection.invoices || []).filter((inv) => inv.is_draft),
		[invoicesSection.invoices],
	);

	const paidInvoices = useMemo(
		() =>
			(invoicesSection.invoices || []).filter(
				(inv) => inv.paid_status === "paid" && !inv.is_draft && !inv.is_void,
			),
		[invoicesSection.invoices],
	);

	// ── Event handlers ────────────────────────────────────────────────────────
const handlePhaseSelect = useCallback(
		(bqeId, recordId) => {
			if (bqeId && bqeId === selectedBqeId) return;
			setSelectedBqeId(bqeId);
			setSelectedPhaseId(recordId);
			setPhaseReportModel(null);
			setSectionStatus(
				PHASE_REPORT_SECTION_CONFIG.reduce((acc, { key, lazy }) => {
					acc[key] = { loading: !lazy, error: "" };
					return acc;
				}, {}),
			);
			const nextParams = new URLSearchParams(searchParams);
			if (bqeId) {
				nextParams.set(PROJECT_QUERY_PARAM, bqeId);
			} else {
				nextParams.delete(PROJECT_QUERY_PARAM);
			}
			setSearchParams(nextParams, { replace: true });
			setPhaseOverviewOpen(false);
			setTimeEntriesEmployeeFilter("All");
			setTimeEntriesDateFrom("");
			setTimeEntriesDateTo("");
			setTimeEntriesBillableFilter("all");
			setExpandedWeeklyReports({});
			setPhotosSortKey("photo_count");
			setPhotosSortDir("desc");
			setPhotosView("all_time");
			setInvoiceListTab("outstanding");
			setDocumentsSubTab("proposals");
			setViewingDocument(null);
		},
		[selectedBqeId, searchParams, setSearchParams],
	);

	const ensureAddDialogDataLoaded = useCallback(() => {
		if (addDialogDataLoaded || addDialogDataLoading) return;
		setAddDialogDataLoading(true);
		Promise.all([
			getAPICall(instance, accounts[0], "/api/jobs/property/all"),
			getAPICall(instance, accounts[0], "/api/employees/all"),
			getAPICall(instance, accounts[0], "/api/jobs/project/all"),
			getAPICall(instance, accounts[0], "/api/jobs/components/all"),
			getAPICall(instance, accounts[0], "/api/jobs/phaseTypes/all"),
			getAPICall(instance, accounts[0], "/api/jobs/phases/names/all"),
			getAPICall(instance, accounts[0], "/api/jobs/phases/all"),
		])
			.then(([propertiesRes, employeesRes, projectsRes, componentsRes, phaseTypesRes, phaseNamesRes, phasesRes]) => {
				setAddDialogProperties(propertiesRes.data || []);
				setAddDialogEmployees(employeesRes.data || []);
				setAddDialogProjects(projectsRes.data || []);
				setAddDialogBuildingComponents(componentsRes.data || []);
				setAddDialogPhaseTypes(phaseTypesRes.data || []);
				setAddDialogPhaseNames(phaseNamesRes.data || []);
				setAddDialogPhases(Array.isArray(phasesRes.data) ? phasesRes.data : []);
				setAddDialogDataLoaded(true);
			})
			.catch((err) => {
				console.error("Failed to load Add Project/Phase reference data:", err);
				setAddDialogDataFailed(true);
			})
			.finally(() => setAddDialogDataLoading(false));
	}, [addDialogDataLoaded, addDialogDataLoading, instance, accounts]);

	// Admins can edit/link Tools records from the hero nav, so this reference data
	// needs to be loaded (not just lazily on Add-menu open) to know whether the
	// currently viewed property/project/phase has a linked Tools record. Loaded once,
	// not on every ensureAddDialogDataLoaded identity change — otherwise a failed
	// load would retry forever. Opening the Add menu retries it.
	useEffect(() => {
		if (props.isAdmin) ensureAddDialogDataLoaded();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isAdmin]);

	const handleAddMenuOpen = (event) => {
		setAddDialogDataFailed(false);
		ensureAddDialogDataLoaded();
		setAddMenuAnchor(event.currentTarget);
	};
	const handleAddMenuClose = () => setAddMenuAnchor(null);
	const handleAddOptionSelect = (type) => {
		setAddMenuAnchor(null);
		if (type === "project") setAddProjectOpen(true);
		if (type === "phase") setAddPhaseOpen(true);
	};

	const closeAddProjectNoRefresh = () => setAddProjectOpen(false);
	const closeAddProjectAndRefresh = () => {
		setAddProjectOpen(false);
		// Refresh the Tools project list so a project just created here shows up
		// immediately in the Phase dialog's "Tools Project Record" picker, without
		// requiring the user to close and reopen the Add menu (which only fetches once).
		getAPICall(instance, accounts[0], "/api/jobs/project/all")
			.then((res) => setAddDialogProjects(res.data || []))
			.catch((err) => console.error("Failed to refresh project list:", err));
	};

	const closeAddPhaseNoRefresh = () => setAddPhaseOpen(false);
	const closeAddPhaseAndRefresh = () => {
		setAddPhaseOpen(false);
		fetchBrowserList();
	};

	const handleEditMenuOpen = (event) => setEditMenuAnchor(event.currentTarget);
	const handleEditMenuClose = () => setEditMenuAnchor(null);
	const handleEditOptionSelect = (type) => {
		setEditMenuAnchor(null);
		if (type === "project") setEditProjectOpen(true);
		if (type === "phase") setEditPhaseOpen(true);
	};

	// Each prefill is what the viewer already knows about the BQE record, shaped like
	// the Tools row the Add dialog would create.
	const handleLinkCreateNew = () => {
		const { type, prefill } = linkChooser;
		setLinkChooser(null);
		if (type === "property") setLinkPropertyPrefill(prefill);
		if (type === "project") setLinkProjectPrefill(prefill);
		if (type === "phase") setLinkPhasePrefill(prefill);
	};
	const handleLinkUseExisting = (record) => {
		const { type, prefill } = linkChooser;
		setLinkChooser(null);
		setRelinkRecord({ type, record: { ...record, bqe_id: prefill.bqe_id } });
	};

	const handleLinkPropertySelect = () => {
		setEditMenuAnchor(null);
		// Property folders sit at "/Projects/<office>/<property>" (or under /Archives),
		// so the phase's own Dropbox path is the best guess for the property's.
		const phasePathParts = String(selectedPhaseRecord?.["Dropbox Path"] || "").split("/");
		const guessedDropboxPath =
			["Projects", "Archives"].includes(phasePathParts[1]) && phasePathParts.length > 3
				? phasePathParts.slice(0, 4).join("/")
				: "";
		const oic = String(selectedPhaseRecord?.["BQE OIC"] || "").trim().toUpperCase();
		setLinkChooser({ type: "property", prefill: {
			bqe_id: selectedPropertyRecord?.["BQE Id"] || null,
			dropbox_file_path: guessedDropboxPath,
			oic: ["WA", "OR", "UT"].includes(oic) ? oic : "",
		} });
	};
	const handleLinkProjectSelect = () => {
		setEditMenuAnchor(null);
		setLinkChooser({ type: "project", prefill: {
			property_id: editablePropertyRecord?.id || phaseReportModel?.local_property_id || null,
			bqe_id: selectedProjectRecord?.["BQE Id"] || null,
			// A Tools phase's hubspot_id is copied from its project, so the phase's
			// deal is the best guess for the project's.
			hubspot_id: selectedPhaseRecord?.["J2 Tools HubSpot Id"] || null,
			display_name: selectedProjectName !== notApplicable ? selectedProjectName : "",
			// The project's own folder name (e.g. "BE (2024)") can't be derived safely,
			// so start from the parent property's folder.
			dropbox_path: editablePropertyRecord?.dropbox_file_path || "",
		} });
	};
	const handleLinkPhaseSelect = () => {
		setEditMenuAnchor(null);
		const bqePhaseName = String(selectedPhaseRecord?.["BQE Name"] || "").trim().toLowerCase();
		const guessedName = addDialogPhaseNames.find(
			(phaseName) => String(phaseName.name || "").trim().toLowerCase() === bqePhaseName
		);
		setLinkChooser({ type: "phase", prefill: {
			property_id: editableProjectRecord?.property_id || phaseReportModel?.local_property_id || null,
			project_id: editableProjectRecord?.id || null,
			name: guessedName?.name || null,
			bqe_id: selectedPhaseRecord?.["BQE Id"] || null,
			dropbox_path: selectedPhaseRecord?.["Dropbox Path"] || editableProjectRecord?.dropbox_path || "",
			company_cam_id: selectedPhaseRecord?.["Company Cam Id"] || "",
		} });
	};
	// Candidate Tools records for "Use existing": any property; for a project or phase,
	// only those under the already-linked Tools property / project. Records not yet
	// linked to any BQE record are listed first, since they're the likeliest match.
	const unlinkedFirst = (records, labelOf) =>
		[...records].sort(
			(a, b) => Boolean(a.bqe_id) - Boolean(b.bqe_id) || labelOf(a).localeCompare(labelOf(b))
		);
	const withBqeCode = (label, record) => (record.bqe_code ? `${label} (${record.bqe_code})` : label);
	const linkChooserProps = () => {
		if (linkChooser?.type === "property") {
			const labelOf = (property) =>
				withBqeCode(
					[property.name, property.address_line1].filter(Boolean).join(" — ") || `Property ${property.id}`,
					property
				);
			return {
				entityLabel: "Property",
				bqeName: selectedPropertyName,
				options: unlinkedFirst(addDialogProperties, labelOf),
				getOptionLabel: labelOf,
				noOptionsText: "No Tools property records found",
			};
		}
		if (linkChooser?.type === "project") {
			const labelOf = (project) =>
				withBqeCode(project.display_name || project.name || `Project ${project.id}`, project);
			return {
				entityLabel: "Project",
				bqeName: selectedProjectName,
				options: unlinkedFirst(
					addDialogProjects.filter((project) => project.property_id === editablePropertyRecord?.id),
					labelOf
				),
				getOptionLabel: labelOf,
				noOptionsText: "No Tools projects under this property",
			};
		}
		const labelOf = (phase) => withBqeCode(phase.name || `Phase ${phase.id}`, phase);
		return {
			entityLabel: "Phase",
			bqeName: selectedPhaseName,
			options: unlinkedFirst(
				addDialogPhases.filter((phase) => phase.project_id === editableProjectRecord?.id),
				labelOf
			),
			getOptionLabel: labelOf,
			noOptionsText: "No Tools phases under this project",
		};
	};

	const closeLinkPropertyNoRefresh = () => {
		setLinkPropertyPrefill(null);
		setRelinkRecord(null);
	};
	const closeLinkPropertyAndRefresh = () => {
		setLinkPropertyPrefill(null);
		setRelinkRecord(null);
		getAPICall(instance, accounts[0], "/api/jobs/property/all")
			.then((res) => setAddDialogProperties(res.data || []))
			.catch((err) => console.error("Failed to refresh property list:", err));
		handleRefreshPhase();
	};
	const closeLinkProjectNoRefresh = () => {
		setLinkProjectPrefill(null);
		setRelinkRecord(null);
	};
	const closeLinkProjectAndRefresh = () => {
		setLinkProjectPrefill(null);
		setRelinkRecord(null);
		getAPICall(instance, accounts[0], "/api/jobs/project/all")
			.then((res) => setAddDialogProjects(res.data || []))
			.catch((err) => console.error("Failed to refresh project list:", err));
		handleRefreshPhase();
	};
	const closeLinkPhaseNoRefresh = () => {
		setLinkPhasePrefill(null);
		setRelinkRecord(null);
	};
	const closeLinkPhaseAndRefresh = () => {
		setLinkPhasePrefill(null);
		setRelinkRecord(null);
		getAPICall(instance, accounts[0], "/api/jobs/phases/all")
			.then((res) => setAddDialogPhases(Array.isArray(res.data) ? res.data : []))
			.catch((err) => console.error("Failed to refresh phase list:", err));
		handleRefreshPhase();
	};

	const closeEditPhaseNoRefresh = () => setEditPhaseOpen(false);
	const closeEditPhaseAndRefresh = () => {
		setEditPhaseOpen(false);
		// Refresh the Tools phase list (for the dialog's own data), the browser list
		// (the phase name may have changed) and the currently viewed phase's report.
		getAPICall(instance, accounts[0], "/api/jobs/phases/all")
			.then((res) => setAddDialogPhases(Array.isArray(res.data) ? res.data : []))
			.catch((err) => console.error("Failed to refresh phase list:", err));
		fetchBrowserList();
		handleRefreshPhase();
	};

	const closeEditProjectNoRefresh = () => setEditProjectOpen(false);
	const closeEditProjectAndRefresh = () => {
		setEditProjectOpen(false);
		// Refresh the Tools project list (for the dialog's own data) and the
		// currently viewed phase's report (so edited fields like owner/property
		// show up immediately in the hero header and At A Glance tab).
		getAPICall(instance, accounts[0], "/api/jobs/project/all")
			.then((res) => setAddDialogProjects(res.data || []))
			.catch((err) => console.error("Failed to refresh project list:", err));
		handleRefreshPhase();
	};

	const setActiveTab = useCallback(
		(tabId) => {
			const nextParams = new URLSearchParams(searchParams);
			nextParams.set(TAB_QUERY_PARAM, tabId);
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	// ─────────────────────────────────────────────────────────────────────────
	// SKELETON
	// ─────────────────────────────────────────────────────────────────────────

	const renderDetailSkeleton = () => (
		<>
			<div className="projectViewerHero projectViewerPaper projectViewerSkeletonCard">
				<div className="projectViewerHeroTop">
					<div className="projectViewerHeroTitleBlock">
						<div className="projectViewerEyebrowRow">
							<div className="projectViewerSkeleton projectViewerSkeletonMeta" />
							<div className="projectViewerSkeletonNavGroup">
								<div className="projectViewerSkeleton projectViewerSkeletonNavArrow" />
								<div className="projectViewerSkeleton projectViewerSkeletonNavPosition" />
								<div className="projectViewerSkeleton projectViewerSkeletonNavArrow" />
							</div>
						</div>
						<div className="projectViewerSkeleton projectViewerSkeletonHeroTitle" />
						<div className="projectViewerSkeletonRow projectViewerSkeletonMetaGrid">
							{[1, 2, 3, 4, 5].map((i) => (
								<div key={i} className="projectViewerSkeletonMetaCard">
									<div className="projectViewerSkeleton projectViewerSkeletonMetaIcon" />
									<div className="projectViewerSkeletonStack">
										<div className="projectViewerSkeleton projectViewerSkeletonMetaValue" />
										<div className="projectViewerSkeleton projectViewerSkeletonMetaLabel" />
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
				<div className="projectViewerSkeletonTabs">
					{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
						<div key={i} className="projectViewerSkeleton projectViewerSkeletonTab" />
					))}
				</div>
			</div>

			<div className="projectViewerTabPanel">
				<div className="projectViewerPaper projectViewerOverview projectViewerSkeletonCard">
					<div className="projectViewerOverviewHeader">
						<div className="projectViewerSkeletonStack projectViewerSkeletonOverviewHeader">
							<div className="projectViewerSkeleton projectViewerSkeletonTitle" />
							<div className="projectViewerSkeleton projectViewerSkeletonMeta projectViewerSkeletonOverviewDate" />
						</div>
						<div className="projectViewerSkeleton projectViewerSkeletonPill" />
					</div>
					<div className="projectViewerOverviewContent">
						<div className="projectViewerSkeletonStack">
							<div className="projectViewerSkeleton projectViewerSkeletonLine" />
							<div className="projectViewerSkeleton projectViewerSkeletonLine" />
							<div className="projectViewerSkeleton projectViewerSkeletonLine short" />
						</div>
					</div>
				</div>
				<div className="projectViewerMainGrid">
					<div className="projectViewerPaper projectViewerSkeletonCard">
						<div className="projectViewerPaperHeader">
							<div className="projectViewerSkeletonStack">
								<div className="projectViewerSkeleton projectViewerSkeletonTitle" />
								<div className="projectViewerSkeleton projectViewerSkeletonMeta projectViewerSkeletonHeaderSubtext" />
							</div>
						</div>
						<div className="projectViewerPaperBody">
							<div className="projectViewerSkeletonKeyValueGrid">
								{[1, 2, 3, 4, 5, 6, 7].map((i) => (
									<React.Fragment key={i}>
										<div className="projectViewerSkeleton projectViewerSkeletonKey" />
										<div className="projectViewerSkeleton projectViewerSkeletonValue" />
									</React.Fragment>
								))}
							</div>
						</div>
					</div>
					<div className="projectViewerPaper projectViewerSkeletonCard">
						<div className="projectViewerPaperHeader">
							<div className="projectViewerSkeletonStack">
								<div className="projectViewerSkeleton projectViewerSkeletonTitle" />
							</div>
						</div>
						<div className="projectViewerPaperBody">
							<div className="projectViewerQuickLinksGrid">
								{[1, 2, 3, 4].map((i) => (
									<div key={i} className="projectViewerSkeletonQuickLink">
										<div className="projectViewerSkeleton projectViewerSkeletonQuickLinkBadge" />
										<div className="projectViewerSkeleton projectViewerSkeletonQuickLinkLabel" />
										<div className="projectViewerSkeleton projectViewerSkeletonQuickLinkArrow" />
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);

	const renderBrowserSkeleton = () => (
		<>
			<div className="projectViewerProjectList projectViewerProjectListSkeleton">
				{[1, 2, 3, 4, 5, 6].map((i) => (
					<div key={i} className="projectViewerProjectListItem projectViewerSkeletonBrowserCard">
						<div className="projectViewerSkeletonStack">
							<div className="projectViewerProjectListTopRow">
								<div className="projectViewerSkeleton projectViewerSkeletonBrowserTitle" />
								<div className="projectViewerSkeleton projectViewerSkeletonBrowserChip" />
							</div>
							<div className="projectViewerProjectListMeta">
								<div className="projectViewerSkeleton projectViewerSkeletonBrowserMeta" />
								<div className="projectViewerSkeleton projectViewerSkeletonBrowserMeta short" />
							</div>
							<div className="projectViewerProjectListFooter">
								<div className="projectViewerSkeleton projectViewerSkeletonBrowserFooter" />
							</div>
						</div>
					</div>
				))}
			</div>
		</>
	);

	// ─────────────────────────────────────────────────────────────────────────
	// TAB RENDER FUNCTIONS
	// ─────────────────────────────────────────────────────────────────────────

	const renderAtAGlanceTab = () => (
		<div className="projectViewerTabPanel">
			<div className="projectViewerPaper projectViewerStatusBanner">
				<div className="projectViewerStatusBannerHeader">
					<div className="projectViewerStatusBannerLabel">
						<PushPinIcon className="projectViewerStatusBannerPin" />
						Current Status
					</div>
					{!isEditingStatus && projectStatusDealId && !statusLoading && (
						statusSaving
							? <span className="projectViewerStatusSaveState">Saving…</span>
							: statusSavedAt
								? <span className="projectViewerStatusSaveState">Saved</span>
								: (
									<Button size="small" variant="text" onClick={startEditingProjectStatus}>
										{statusText ? "Edit" : "Add Update"}
									</Button>
								)
					)}
				</div>
				{!projectStatusDealId ? (
					<p className="projectViewerStatusBannerPlaceholder">No HubSpot deal linked to this project.</p>
				) : statusLoading ? (
					renderInlineLoadingNotice("Loading status update...")
				) : !isEditingStatus ? (
					statusText
						? <p className="projectViewerStatusBannerText">{statusText}</p>
						: <p className="projectViewerStatusBannerPlaceholder">No status update yet.</p>
				) : (
					<>
						<TextField
							className="projectViewerStatusBannerField"
							value={statusDraft}
							onChange={(e) => setStatusDraft(e.target.value)}
							placeholder="Write a status update for this project…"
							variant="outlined"
							multiline
							minRows={3}
							fullWidth
							autoFocus
						/>
						<div className="projectViewerStatusFooter">
							{statusError && <span className="projectViewerStatusError">{statusError}</span>}
							<span style={{ flex: 1 }} />
							<Button size="small" variant="text" disabled={statusSaving} onClick={cancelEditingProjectStatus}>
								Cancel
							</Button>
							<Button size="small" variant="contained" disabled={statusSaving} onClick={saveProjectStatusUpdate}>
								{statusSaving ? "Saving…" : "Save to HubSpot"}
							</Button>
						</div>
					</>
				)}
			</div>

			<div className="projectViewerPaper projectViewerOverview">
				<div className="projectViewerOverviewHeader">
					<div className="projectViewerOverviewHeadingBlock">
						<div className="projectViewerEyebrow">AI Overview</div>
						<div className="projectViewerOverviewDate">
							{sectionStatus["at_a_glance"]?.loading
								? "Report period loading..."
								: (reportingPeriodDisplay ? `Report period: ${reportingPeriodDisplay}` : "")}
						</div>
					</div>
					<button
						type="button"
						className={`projectViewerCollapseToggle${
							phaseOverviewOpen ? " open" : ""
						}`}
						onClick={() => setPhaseOverviewOpen((current) => !current)}
						aria-label={
							phaseOverviewOpen
								? "Collapse AI overview"
								: "Expand AI overview"
						}
					>
						<span className="projectViewerCollapseLabel">
							{phaseOverviewOpen ? "Hide" : "Show"}
						</span>
						<ExpandMoreIcon fontSize="inherit" />
					</button>
				</div>
				{phaseOverviewOpen && (
					<div className="projectViewerOverviewContent">
						{sectionStatus["at_a_glance"]?.loading ? (
							renderInlineLoadingNotice("Loading AI overview...")
						) : sectionStatus["at_a_glance"]?.error ? (
							renderSectionErrorPanel(sectionStatus["at_a_glance"].error)
						) : (
							<>
								<div className="projectViewerNarrative">
									{atAGlanceSection?.phase_overview_summary ? (
										<p>{atAGlanceSection.phase_overview_summary}</p>
									) : (
										<p className="projectViewerEmptyNotice">No automated reports have been generated for this phase yet.</p>
									)}
								</div>
								{atAGlanceSection?.phase_overview_summary && (
									<div className="projectViewerOverviewDisclaimer">
										Overview generated using ChatGPT, please double check all
										information in this section.
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>

			<div className="projectViewerMainGrid projectViewerContactsCommGrid">
				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
						<div>
							<h3>Project Summary</h3>
							<p>
								{reportingPeriodDisplay
									? `Report period: ${reportingPeriodDisplay}`
									: ""}
							</p>
						</div>
						<Button
							size="small"
							variant="outlined"
							startIcon={<EditIcon />}
							onClick={() => setStatusDialogOpen(true)}
						>
							Edit
						</Button>
					</div>
					<div className="projectViewerPaperBody">
						{sectionStatus["at_a_glance"]?.loading ? (
							renderInlineLoadingNotice("Loading project summary...")
						) : sectionStatus["at_a_glance"]?.error ? (
							renderSectionErrorPanel(sectionStatus["at_a_glance"].error)
						) : (
							<>
								{(!hubspotDealUrl || (phaseReportModel !== null && !phaseReportModel.phase_bqe_id)) && renderMissingInfoNotice(
									"Some information in this summary may be incomplete."
								)}
							<div className="projectViewerKeyValueGrid">
								<div className="projectViewerKey">Property</div>
								<div className="projectViewerValue">
									{phaseReportModel?.local_property_id ? (
										<a
											href={`/properties/viewer?recordId=${encodeURIComponent(phaseReportModel.local_property_id)}&tab=overview`}
											target="_blank"
											rel="noopener noreferrer"
											className="projectViewerTableLink"
										>
											{selectedPropertyName}
										</a>
									) : (
										selectedPropertyName
									)}
								</div>
								<div className="projectViewerKey">Property Address</div>
								<div className="projectViewerValue">
									{atAGlanceSection?.property_address || notApplicable}
								</div>
								<div className="projectViewerKey">Project Manager</div>
								<div className="projectViewerValue">
									{atAGlanceSection?.project_manager || notApplicable}
								</div>
								<div className="projectViewerKey">Project Client</div>
								<div className="projectViewerValue">
									{atAGlanceSection?.client || notApplicable}
								</div>
								<div className="projectViewerKey">BQE Status</div>
								<div className="projectViewerValue">
									{selectedPhaseRecord?.["BQE Status"] || atAGlanceSection?.phase_status || notApplicable}
								</div>
								<div className="projectViewerKey">{isAmendmentPhase ? "Amendment Type" : "Contract Type"}</div>
								<div className="projectViewerValue">
									{selectedPhaseRecord?.["BQE Contract Type"] || atAGlanceSection?.contract_type || notApplicable}
								</div>
								<div className="projectViewerKey">{isAmendmentPhase ? "Amendment Amount" : "Contract Amount"}</div>
								<div className="projectViewerValue">
									{atAGlanceSection?.contract_amount != null ? formatCurrency(atAGlanceSection.contract_amount) : notApplicable}
								</div>
								{isAmendmentPhase && (
									<>
										<div className="projectViewerKey">Amended Phase</div>
										<div className="projectViewerValue">
											{phaseReportModel?.amended_phase?.no_record ? (
												<Tooltip arrow title="No amendment in Tools is filed under this phase's BQE project — it was likely set up directly in BQE or before amendments were tracked in Tools. The HubSpot link is left off since the deal can't be confirmed.">
													<span className="projectViewerClosedPhaseName">No amendment record in Tools</span>
												</Tooltip>
											) : phaseReportModel?.amended_phase?.bqe_id && linkedPhaseClosedNote(phaseReportModel.amended_phase) ? (
												<Tooltip arrow title={linkedPhaseClosedNote(phaseReportModel.amended_phase)}>
													<span className="projectViewerClosedPhaseName">
														{linkedPhaseName(phaseReportModel.amended_phase)}
														<span className="projectViewerClosedPhaseStatus">
															{phaseReportModel.amended_phase.is_deleted ? "Deleted" : phaseReportModel.amended_phase.status || "Closed"}
														</span>
													</span>
												</Tooltip>
											) : phaseReportModel?.amended_phase?.bqe_id ? (
												<a
													href={`/projects/viewer?recordId=${encodeURIComponent(phaseReportModel.amended_phase.bqe_id)}&tab=atAGlance`}
													target="_blank"
													rel="noopener noreferrer"
													className="projectViewerTableLink"
												>
													{linkedPhaseName(phaseReportModel.amended_phase)}
												</a>
											) : (
												notApplicable
											)}
										</div>
									</>
								)}
								<div className="projectViewerKey">Budgeted Total</div>
								<div className="projectViewerValue">
									{budgetMoneyLoading ? <CircularProgress size={12} thickness={5} />
										: budgetMoney.hasBudget ? formatCurrency(budgetMoney.budgetedTotal) : "No Budget"}
								</div>
								<div className="projectViewerKey" style={{ display: "flex", alignItems: "center", gap: 4 }}>
									Spent To Date
									<Tooltip arrow title="All time (billable and non-billable) and posted expenses on this phase, in the budget or not — the same basis as BQE's budget spent. Each time entry is priced at its own bill rate.">
										<InfoOutlinedIcon className="projectViewerInfoIcon" />
									</Tooltip>
								</div>
								<div
									className="projectViewerValue"
									style={{
										fontWeight: 600,
										color: atAGlanceSection?.contract_amount != null && budgetMoney.spentTotal > atAGlanceSection.contract_amount ? "#c62828"
											: budgetMoney.hasBudget && budgetMoney.spentTotal > budgetMoney.budgetedTotal ? "#b45309"
											: undefined,
									}}
								>
									{budgetMoneyLoading || unbudgetedPricing.loading ? <CircularProgress size={12} thickness={5} /> : (
										<>
											{formatCurrency(budgetMoney.spentTotal)}
											<span style={{ fontWeight: 400, color: "#666" }}>
												{` (Services ${formatCurrency(budgetMoney.spentServices)} + Expenses ${formatCurrency(budgetMoney.spentExpenses)}`}
												{budgetMoney.unpricedHours > 0 ? `; ${budgetMoney.unpricedHours.toFixed(1)} hrs with no rate found` : ""}
												{")"}
											</span>
										</>
									)}
								</div>
								<div className="projectViewerKey">Amount Invoiced</div>
								<div className="projectViewerValue">
									{atAGlanceSection?.invoice_summary?.total_invoice_amount != null
										? formatCurrency(atAGlanceSection.invoice_summary.total_invoice_amount)
										: notApplicable}
								</div>
								<div className="projectViewerKey">Amount Paid</div>
								<div className="projectViewerValue">
									{atAGlanceSection?.invoice_summary?.total_paid != null
										? formatCurrency(atAGlanceSection.invoice_summary.total_paid)
										: notApplicable}
								</div>
								<div className="projectViewerKey">Budget</div>
								<div className={`projectViewerValue projectViewerSummaryStatus${
									budgetIndicator?.status === "Over Budget" ? " danger" :
									budgetIndicator?.status === "Under Budget" || budgetIndicator?.status === "On Budget" ? " good" :
									" danger"
								}`}>
									{(() => {
										const { status, variance_amount, budgeted_amount, spent_amount } = budgetIndicator || {};
										if (!status || status === "Unknown") return "No Budget";
										const parts = [status];
										if (variance_amount != null && budgeted_amount != null && spent_amount != null) {
											const usage = `${formatCurrency(spent_amount)} spent of ${formatCurrency(budgeted_amount)} budget`;
											if (status === "Over Budget") {
												parts.push(`${formatCurrency(Math.abs(variance_amount))} over (${usage})`);
											} else if (status === "Under Budget") {
												parts.push(`${formatCurrency(variance_amount)} remaining (${usage})`);
											} else {
												parts.push(usage);
											}
										}
										return parts.join(" — ");
									})()}
								</div>
								<div className="projectViewerKey">Amendments</div>
								<div className="projectViewerValue">
									{(() => {
										const amend = atAGlanceSection?.amendment_summary;
										if (!amend) return notApplicable;
										if (amend.amendment_count === 0) return "No amendments";
										const parts = [];
										if (amend.submitted_count > 0) parts.push(`${amend.submitted_count} submitted (${formatCurrency(amend.total_amendment_amount)})`);
										if (amend.pending_count > 0) parts.push(`${amend.pending_count} in progress`);
										return parts.join(" · ") || "No amendments";
									})()}
								</div>
								{!isAmendmentPhase && (
									<>
										<div className="projectViewerKey">Proposal Sent Date</div>
										<div className="projectViewerValue">
											{formatDate(atAGlanceSection?.proposal_sent_date)}
										</div>
										<div className="projectViewerKey">Proposal Signed Date</div>
										<div className="projectViewerValue">
											{formatDate(atAGlanceSection?.proposal_signed_date)}
										</div>
									</>
								)}
								<div className="projectViewerKey">Start Date</div>
								<div className="projectViewerValue">
									{formatDate(schedDur.start_date || selectedPhaseRecord?.["CF-Start Date"])}
								</div>
								<div className="projectViewerKey">Duration</div>
								<div className="projectViewerValue">
									{schedDur.duration_weeks != null ? `${schedDur.duration_weeks} wks` : notApplicable}
								</div>
								<div className="projectViewerKey">End Date</div>
								<div className="projectViewerValue">
									{formatDate(schedDur.estimated_end_date || schedDur.end_date || selectedPhaseRecord?.["CF-End Date"])}
								</div>
								<div className="projectViewerKey">Invoices</div>
								<div className={`projectViewerValue projectViewerSummaryStatus${
									atAGlanceSection?.invoice_summary?.overdue_count > 0 ? " danger" :
									atAGlanceSection?.invoice_summary?.unpaid_count > 0 ? " warning" :
									atAGlanceSection?.unpaid_invoice_indicator === false ? " good" : ""
								}`}>
									{(() => {
										const inv = atAGlanceSection?.invoice_summary;
										if (!inv) return notApplicable;
										if (inv.unpaid_count === 0) return "No unpaid invoices";
										const parts = [`${inv.unpaid_count} unpaid`];
										if (inv.overdue_count > 0) parts.push(`${inv.overdue_count} overdue`);
										if (inv.total_balance > 0) parts.push(`${formatCurrency(inv.total_balance)} outstanding`);
										return parts.join(" · ");
									})()}
								</div>
							</div>
							</>
						)}
					</div>
				</div>

				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader">
						<div>
							<h3>Quick Links</h3>
						</div>
					</div>
					<div className="projectViewerPaperBody">
						<div className="projectViewerQuickLinksGrid">
							<a
								className={`projectViewerQuickLinkItem${!hubspotDealUrl ? " disabled" : ""}`}
								href={hubspotDealUrl || undefined}
								target="_blank"
								rel="noreferrer"
							>
								<div className="projectViewerIntegrationBadge hubspot">
									HS
								</div>
								<div className="projectViewerQuickLinkTitle">HubSpot</div>
								<LaunchIcon fontSize="small" />
							</a>
							{!isAmendmentPhase && (
								<a
									className={`projectViewerQuickLinkItem${!companyCamUrl ? " disabled" : ""}`}
									href={companyCamUrl || undefined}
									target="_blank"
									rel="noreferrer"
								>
									<div className="projectViewerIntegrationBadge ccam">CC</div>
									<div className="projectViewerQuickLinkTitle">CompanyCam</div>
									<LaunchIcon fontSize="small" />
								</a>
							)}
							<a
								className={`projectViewerQuickLinkItem${!bqeUrl ? " disabled" : ""}`}
								href={bqeUrl || undefined}
								target="_blank"
								rel="noreferrer"
							>
								<div className="projectViewerIntegrationBadge bqe">BQ</div>
								<div className="projectViewerQuickLinkTitle">BQE</div>
								<LaunchIcon fontSize="small" />
							</a>
							{!isAmendmentPhase && (
								<a
									className={`projectViewerQuickLinkItem${!dropboxPath ? " disabled" : ""}`}
									href={dropboxPath ? dropboxFolderUrl : undefined}
									target="_blank"
									rel="noreferrer"
								>
									<div className="projectViewerIntegrationBadge dropbox">
										DB
									</div>
									<div className="projectViewerQuickLinkTitle">Dropbox</div>
									<LaunchIcon fontSize="small" />
								</a>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const renderContactsTab = () => (
		<div className="projectViewerTabPanel">
			{atAGlanceLoaded && !hubspotDealUrl && renderMissingInfoNotice(
				"Contact and communication information is incomplete for this phase."
			)}
			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<h3>Linked Contacts</h3>
						<p>Up To Date</p>
					</div>
					<a
						className={`projectViewerQuickLinkItem projectViewerInlineDealLink${!hubspotDealUrl ? " disabled" : ""}`}
						href={hubspotDealUrl || undefined}
						target="_blank"
						rel="noreferrer"
					>
						<div className="projectViewerIntegrationBadge hubspot">HS</div>
						<div className="projectViewerQuickLinkTitle">HubSpot Deal</div>
						<LaunchIcon fontSize="small" />
					</a>
				</div>
				<div className="projectViewerPaperBody">
					{sectionStatus["contacts"]?.loading ? (
						renderInlineLoadingNotice("Loading contacts...")
					) : sectionStatus["contacts"]?.error ? (
						renderSectionErrorPanel(sectionStatus["contacts"].error)
					) : (
						<>
							{canViewServiceRecovery && (
							<div className="projectViewerServiceRecoveryNote">
								<div className="projectViewerServiceRecoveryHeader">
									<div className="projectViewerServiceRecoveryTitle">
										Service Recovery Notes
									</div>
									<button
										type="button"
										className={`projectViewerCollapseToggle projectViewerInlineCollapse${
											serviceRecoveryOpen ? " open" : ""
										}`}
										onClick={() =>
											setServiceRecoveryOpen((current) => !current)
										}
										aria-label={
											serviceRecoveryOpen
												? "Collapse service recovery notes"
												: "Expand service recovery notes"
										}
									>
										<span className="projectViewerCollapseLabel">
											{serviceRecoveryOpen ? "Hide" : "Show"}
										</span>
										<ExpandMoreIcon fontSize="inherit" />
									</button>
								</div>
								{serviceRecoveryOpen && (
									<div className="projectViewerServiceRecoveryBody">
										{contactsSection?.service_recovery_notes
											? contactsSection.service_recovery_notes
											: <span className="projectViewerEmptyNotice">No service recovery notes have been recorded for this phase.</span>
										}
									</div>
								)}
							</div>
						)}
							<table className="projectViewerTable">
								<thead>
									<tr>
										<th>Name</th>
										<th>Email</th>
										<th>Phone</th>
										<th>Mobile Phone #</th>
										<th>Associated Deals</th>
										<th>Emails Sent</th>
										<th>Calls</th>
									</tr>
								</thead>
								<tbody>
									{(contactsSection?.contacts || []).map((contact) => (
										<tr key={contact.contact_record_id || contact.email}>
											<td>
												<a
													className={`projectViewerTableLink${!contact.hubspot_id ? " disabled" : ""}`}
													href={
														contact.hubspot_id
															? `https://app.hubspot.com/contacts/9144184/record/0-1/${contact.hubspot_id}`
															: undefined
													}
													target="_blank"
													rel="noreferrer"
												>
													{contact.full_name ||
														`${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
														notApplicable}
												</a>
											</td>
											<td>{contact.email || notApplicable}</td>
											<td>{contact.phone || notApplicable}</td>
											<td>{contact.mobile_phone || notApplicable}</td>
											<td>
												{contact.number_of_associated_deals ?? notApplicable}
											</td>
											<td>{contact.emails_sent || "—"}</td>
											<td>{contact.calls_last_week || "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</>
					)}
				</div>
			</div>

			<div className="projectViewerPaper projectViewerContactsCommCard">
				<div className="projectViewerPaperHeader projectViewerCommHeader">
					<div>
						<div className="projectViewerHeaderTitleRow">
							<h3>Communication</h3>
							<Tooltip
								title="This data comes from HubSpot activities, it may not be a completely accurate record of all project communication."
								arrow
							>
								<InfoOutlinedIcon className="projectViewerInfoIcon" />
							</Tooltip>
						</div>
						<p>Up To Date</p>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
						<div className="projectViewerMiniTabs" role="group" aria-label="Time range">
							{["all", "last_week"].map((val) => (
								<button
									key={val}
									type="button"
									className={`projectViewerMiniTab${commTimeRange === val ? " active" : ""}`}
									onClick={() => setCommTimeRange(val)}
								>
									{val === "all" ? "All" : (reportingPeriodDisplay ? `Report Period (${reportingPeriodDisplay})` : "Report Period")}
								</button>
							))}
						</div>
						<div className="projectViewerMiniTabs" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={contactsCommunicationTab === "email"}
							className={`projectViewerMiniTab${
								contactsCommunicationTab === "email" ? " active" : ""
							}`}
							onClick={() => setContactsCommunicationTab("email")}
						>
							Email
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={contactsCommunicationTab === "phone"}
							className={`projectViewerMiniTab${
								contactsCommunicationTab === "phone" ? " active" : ""
							}`}
							onClick={() => setContactsCommunicationTab("phone")}
						>
							Phone
						</button>
						</div>
					</div>
				</div>
				<div className="projectViewerPaperBody projectViewerCompactTableBody">
					{sectionStatus["contacts"]?.loading ? (
						renderInlineLoadingNotice("Loading communication data...")
					) : sectionStatus["contacts"]?.error ? (
						renderSectionErrorPanel(sectionStatus["contacts"].error)
					) : contactsCommunicationTab === "email" ? (
						<>
							<div className="projectViewerTableFilters">
								<input
									type="text"
									className="projectViewerFilterSearch"
									placeholder="Search by sender, recipient, or subject..."
									value={emailCommSearch}
									onChange={(e) => setEmailCommSearch(e.target.value)}
								/>
								{commTimeRange === "all" && <>
								<label className="projectViewerFilterDateLabel">
									From
									<input
										type="date"
										className="projectViewerFilterDate"
										value={emailCommDateFrom}
										onChange={(e) => setEmailCommDateFrom(e.target.value)}
									/>
								</label>
								<label className="projectViewerFilterDateLabel">
									To
									<input
										type="date"
										className="projectViewerFilterDate"
										value={emailCommDateTo}
										onChange={(e) => setEmailCommDateTo(e.target.value)}
									/>
								</label>
								</>}
							</div>
							<table className="projectViewerTable">
								<colgroup>
									<col style={{ width: "110px" }} />
									<col style={{ width: "200px" }} />
									<col style={{ width: "240px" }} />
									<col style={{ width: "80px" }} />
									<col />
									<col style={{ width: "88px" }} />
								</colgroup>
								<thead>
									<tr>
										<th>Last Email</th>
										<th>Sender</th>
										<th>Recipients</th>
										<th>Messages</th>
										<th>Subject</th>
										<th>Details</th>
									</tr>
								</thead>
								<tbody>
									{filteredEmailThreads.length === 0 ? (
										<tr>
											<td colSpan="6" className="projectViewerEmptyCell">
												{emailCommSearch.trim() ? "No emails match your search." : "No email communication found."}
											</td>
										</tr>
									) : (
										filteredEmailThreads.flatMap((entry) => {
											const rowKey = `${entry.sender}-${entry.last_sent}`;
											const isExpanded = expandedEmailThreads[rowKey];
											const rows = [
												<tr key={rowKey}>
													<td>{entry.last_sent ? new Date(entry.last_sent).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</td>
													<td>{entry.sender}</td>
													<td>{(entry.recipients || []).join(", ")}</td>
													<td>{entry.message_count ?? 1}</td>
													<td>{entry.subject}</td>
													<td>
														<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px" }}>
															{entry.body && (
																<Tooltip title={isExpanded ? "Hide email body" : "View email body"} arrow>
																	<IconButton size="small" onClick={() => toggleEmailThread(rowKey)} color={isExpanded ? "primary" : "default"}>
																		<ArticleOutlinedIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															)}
															{entry.hubspot_url && (
																<Tooltip title="Open in HubSpot" arrow>
																	<IconButton size="small" component="a" href={entry.hubspot_url} target="_blank" rel="noreferrer">
																		<LaunchIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															)}
														</div>
													</td>
												</tr>,
											];
											if (isExpanded && entry.body) {
												rows.push(
													<tr key={rowKey + "-body"}>
														<td colSpan={6} style={{ padding: "10px 16px 14px", background: "rgba(0,0,0,0.02)", borderTop: "none" }}>
															<pre style={{ margin: 0, fontFamily: "inherit", fontSize: "13px", color: "rgba(0,0,0,0.75)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{entry.body}</pre>
														</td>
													</tr>
												);
											}
											return rows;
										})
									)}
								</tbody>
							</table>
						</>
					) : (
						<>
							<div className="projectViewerTableFilters">
								<input
									type="text"
									className="projectViewerFilterSearch"
									placeholder="Search by contact or status..."
									value={phoneCommSearch}
									onChange={(e) => setPhoneCommSearch(e.target.value)}
								/>
								{commTimeRange === "all" && <>
								<label className="projectViewerFilterDateLabel">
									From
									<input
										type="date"
										className="projectViewerFilterDate"
										value={phoneCommDateFrom}
										onChange={(e) => setPhoneCommDateFrom(e.target.value)}
									/>
								</label>
								<label className="projectViewerFilterDateLabel">
									To
									<input
										type="date"
										className="projectViewerFilterDate"
										value={phoneCommDateTo}
										onChange={(e) => setPhoneCommDateTo(e.target.value)}
									/>
								</label>
								</>}
							</div>
							<table className="projectViewerTable" style={{ tableLayout: "fixed", width: "100%" }}>
								<colgroup>
									<col style={{ width: "100px" }} />
									<col style={{ width: "150px" }} />
									<col style={{ width: "140px" }} />
									<col style={{ width: "90px" }} />
									<col style={{ width: "80px" }} />
									<col />
									<col style={{ width: "80px" }} />
								</colgroup>
								<thead>
									<tr>
										<th>Date</th>
										<th>Contact</th>
										<th>J2 Staff</th>
										<th>Direction</th>
										<th>Duration</th>
										<th>Notes</th>
										<th>Details</th>
									</tr>
								</thead>
								<tbody>
									{filteredPhoneCalls.length === 0 ? (
										<tr>
											<td colSpan="7" className="projectViewerEmptyCell">
												{phoneCommSearch.trim() ? "No calls match your search." : "No phone communication found."}
											</td>
										</tr>
									) : (
										filteredPhoneCalls.flatMap((entry, i) => {
											const rowKey = `${entry.contact}-${entry.date}-${i}`;
											const isExpanded = expandedCallSummaries[rowKey];
											const rows = [
												<tr key={rowKey}>
													<td>{entry.date ? new Date(entry.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</td>
													<td>{entry.contact}</td>
													<td>{entry.j2_user}</td>
													<td>{entry.direction}</td>
													<td>{entry.duration}</td>
													<td>{entry.body}</td>
													<td>
														<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px" }}>
															{entry.has_recording && (
																<Tooltip title={listeningCallId === entry.call_id ? "Close recording" : "Play recording"} arrow>
																	<IconButton
																		size="small"
																		onClick={() => setListeningCallId(listeningCallId === entry.call_id ? null : entry.call_id)}
																		color={listeningCallId === entry.call_id ? "primary" : "default"}
																	>
																		<PlayCircleIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															)}
															{entry.summary && (
																<Tooltip title={isExpanded ? "Hide AI summary" : "View AI summary"} arrow>
																	<IconButton
																		size="small"
																		onClick={() => toggleCallSummary(rowKey)}
																		color={isExpanded ? "primary" : "default"}
																	>
																		<AutoAwesomeIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															)}
															{entry.call_url && (
																<Tooltip title="Open in HubSpot" arrow>
																	<IconButton
																		size="small"
																		component="a"
																		href={entry.call_url}
																		target="_blank"
																		rel="noreferrer"
																	>
																		<LaunchIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															)}
														</div>
													</td>
												</tr>,
											];
											if (listeningCallId === entry.call_id) {
												rows.push(
													<tr key={rowKey + "-audio"}>
														<td colSpan={7} style={{ padding: "8px 16px 12px", background: "rgba(0,0,0,0.02)", borderTop: "none" }}>
															{audioBlobUrl
									? <audio controls autoPlay style={{ width: "100%", height: "36px" }} src={audioBlobUrl} />
									: <span style={{ fontSize: "13px", color: "rgba(0,0,0,0.5)" }}>Loading recording…</span>
								}
														</td>
													</tr>
												);
											}
											if (isExpanded && entry.summary) {
												rows.push(
													<tr key={rowKey + "-summary"}>
														<td colSpan={7} style={{ padding: "8px 16px 12px", background: "rgba(0,0,0,0.02)", fontSize: "13px", color: "rgba(0,0,0,0.7)", borderTop: "none" }}>
															{entry.summary}
														</td>
													</tr>
												);
											}
											return rows;
										})
									)}
								</tbody>
							</table>
						</>
					)}
				</div>
			</div>
		</div>
	);

	// Linked phases that aren't Active / On Hold aren't in the viewer's list, so
	// they're shown by name only, with this explanation on hover.
	const linkedPhaseClosedNote = (linkedPhase) =>
		!linkedPhase || linkedPhase.is_open !== false ? ""
		: linkedPhase.is_deleted ? "It's been deleted in BQE, so it can't be opened in the Project Viewer."
		: `It's ${linkedPhase.status ? linkedPhase.status.toLowerCase() : "closed"} in BQE, so it can't be opened in the Project Viewer.`;
	const linkedPhaseName = (linkedPhase) =>
		linkedPhase?.name || (linkedPhase?.is_deleted ? "Deleted phase" : "Phase");

	const renderAmendmentScopeBadge = (amendment) => {
		const scopePhaseName = linkedPhaseName(amendment.scope_phase);
		const closedNote = linkedPhaseClosedNote(amendment.scope_phase);
		const scopePhaseChip = (variant) =>
			closedNote ? (
				<span className={`projectViewerScopeBadge projectViewerScopeLink closed ${variant}`}>
					<span className="projectViewerScopeLinkName">{scopePhaseName}</span>
				</span>
			) : (
				<a
					href={`/projects/viewer?recordId=${encodeURIComponent(amendment.scope_phase.bqe_id)}&tab=amendments`}
					target="_blank"
					rel="noopener noreferrer"
					className={`projectViewerScopeBadge projectViewerScopeLink ${variant}`}
				>
					<span className="projectViewerScopeLinkName">{scopePhaseName}</span>
					<LaunchIcon className="projectViewerScopeLinkIcon" />
				</a>
			);

		// Filed against another phase: name it and link to it.
		if (amendment.scope === "other_phase" && amendment.scope_phase?.bqe_id) {
			return (
				<Tooltip arrow title={closedNote ? `Amends ${scopePhaseName}. ${closedNote}` : `Amends ${scopePhaseName} — open that phase`}>
					{scopePhaseChip("otherPhase")}
				</Tooltip>
			);
		}
		// Amends this phase, but the money is on an amendment phase: link to that.
		if (amendment.scope === "this_phase_via_amendment" && amendment.scope_phase?.bqe_id) {
			return (
				<Tooltip arrow title={`Amends this phase; the amount is on amendment phase ${scopePhaseName}.${closedNote ? ` ${closedNote}` : ""}`}>
					<span className="projectViewerScopeGroup">
						<span className="projectViewerScopeBadge phaseSpecific">This Phase</span>
						<span className="projectViewerScopeArrow" aria-hidden="true">→</span>
						{scopePhaseChip("amendmentPhase")}
					</span>
				</Tooltip>
			);
		}

		const scope = amendment.scope || (amendment.is_phase_specific ? "this_phase" : "project_wide");
		const { label, variant, tip } = {
			this_amendment: { label: "This Amendment", variant: "amendmentPhase", tip: "The amount is on this amendment phase" },
			this_phase: { label: "This Phase", variant: "phaseSpecific", tip: "Added to this phase's contract" },
		}[scope] || { label: "Project-Wide", variant: "projectWide", tip: "Not tied to a specific phase yet" };
		return (
			<Tooltip arrow title={tip}>
				<span className={`projectViewerScopeBadge ${variant}`}>{label}</span>
			</Tooltip>
		);
	};

	// Amendment phases: totals for just the amendments whose amount is on this
	// amendment phase (scope "this_amendment"), alongside the project-wide totals.
	const renderThisAmendmentTotals = () => {
		const isThisAmendment = (a) => a.scope === "this_amendment";
		const submitted = (amendmentsSection?.submitted_amendments || []).filter(isThisAmendment);
		const inProgress = (amendmentsSection?.pending_amendments || []).filter(isThisAmendment);
		const submittedAmount = submitted.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
		return (
			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader">
					<div>
						<h3>This Amendment</h3>
						<p>Amendments whose amount is on this amendment phase</p>
					</div>
				</div>
				<div className="projectViewerPaperBody">
					{sectionStatus["amendments"]?.loading ? (
						renderInlineLoadingNotice("Loading amendments...")
					) : sectionStatus["amendments"]?.error ? (
						renderSectionErrorPanel(sectionStatus["amendments"].error)
					) : (
						<div className="projectViewerKeyValueGrid">
							<div className="projectViewerKey">Submitted Amendments</div>
							<div className="projectViewerValue">{submitted.length}</div>
							<div className="projectViewerKey">Submitted Amendment Amount</div>
							<div className="projectViewerValue">{formatCurrency(submittedAmount)}</div>
							<div className="projectViewerKey">Amendments In Progress</div>
							<div className="projectViewerValue">{inProgress.length}</div>
							<div className="projectViewerKey" style={{ display: "flex", alignItems: "center", gap: 4 }}>
								Amendment Amount In BQE
								<Tooltip arrow title="This amendment phase's contract amount in BQE. It should match the submitted amendment amount above; a difference usually means the BQE phase was changed by hand.">
									<InfoOutlinedIcon className="projectViewerInfoIcon" />
								</Tooltip>
							</div>
							<div className="projectViewerValue">
								{atAGlanceSection?.contract_amount != null ? formatCurrency(atAGlanceSection.contract_amount) : notApplicable}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderAmendmentsTab = () => (
		<div className="projectViewerTabPanel">
			{atAGlanceLoaded && !hubspotDealUrl && renderMissingInfoNotice(
				"Amendment information may be incomplete for this phase."
			)}
			{/* Amendment phases show project totals and this amendment's totals side by side. */}
			<div className={isAmendmentPhase ? "projectViewerTwoColumnGrid" : undefined}>
				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader">
						<div>
							<h3>Amendments</h3>
							<p>Up To Date</p>
						</div>
					</div>
					<div className="projectViewerPaperBody">
						{sectionStatus["amendments"]?.loading ? (
							renderInlineLoadingNotice("Loading amendments...")
						) : sectionStatus["amendments"]?.error ? (
							renderSectionErrorPanel(sectionStatus["amendments"].error)
						) : (
							<div className="projectViewerKeyValueGrid">
								<div className="projectViewerKey">Total Submitted Amendments</div>
								<div className="projectViewerValue">
									{amendmentsSection?.submitted_count ?? notApplicable}
								</div>
								<div className="projectViewerKey">Total Submitted Amendment Amount</div>
								<div className="projectViewerValue">
									{amendmentsSection?.total_amendment_amount != null
										? formatCurrency(amendmentsSection.total_amendment_amount)
										: notApplicable}
								</div>
								<div className="projectViewerKey">Total Amendments In Progress</div>
								<div className="projectViewerValue">
									{amendmentsSection?.pending_count ?? notApplicable}
								</div>
							</div>
						)}
					</div>
				</div>

				{isAmendmentPhase && renderThisAmendmentTotals()}
			</div>

			<div className="projectViewerTwoColumnGrid" style={{ paddingTop: "16px" }}>
				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader">
						<div>
							<h3>Submitted Amendments</h3>
						</div>
					</div>
					<div className="projectViewerPaperBody projectViewerCompactTableBody">
						{sectionStatus["amendments"]?.loading ? (
							renderInlineLoadingNotice("Loading submitted amendments...")
						) : sectionStatus["amendments"]?.error ? (
							renderSectionErrorPanel(sectionStatus["amendments"].error)
						) : (
							<table className="projectViewerTable">
								<thead>
									<tr>
										<th>Type</th>
										<th>Amount</th>
										<th>Submitted On</th>
										<th>Scope</th>
									</tr>
								</thead>
								<tbody>
									{(amendmentsSection?.submitted_amendments || []).length === 0 ? (
										<tr>
											<td colSpan="4" className="projectViewerEmptyCell">
												No Amendments.
											</td>
										</tr>
									) : (
										(amendmentsSection?.submitted_amendments || []).map((amendment) => (
											<tr key={amendment.id}>
												<td>{amendment.is_time_and_materials ? "T&M" : "Fixed Fee"}</td>
												<td>{formatCurrency(amendment.amount)}</td>
												<td>{formatTimestamp(amendment.submitted_on)}</td>
												<td>{renderAmendmentScopeBadge(amendment)}</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						)}
					</div>
				</div>

				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader">
						<div>
							<h3>Amendments In Progress</h3>
						</div>
					</div>
					<div className="projectViewerPaperBody projectViewerCompactTableBody">
						{sectionStatus["amendments"]?.loading ? (
							renderInlineLoadingNotice("Loading amendments in progress...")
						) : sectionStatus["amendments"]?.error ? (
							renderSectionErrorPanel(sectionStatus["amendments"].error)
						) : (
							<table className="projectViewerTable">
								<thead>
									<tr>
										<th>Type</th>
										<th>Amount</th>
										<th>Submitted On</th>
										<th>Scope</th>
									</tr>
								</thead>
								<tbody>
									{(amendmentsSection?.pending_amendments || []).length === 0 ? (
										<tr>
											<td colSpan="4" className="projectViewerEmptyCell">
												No Amendments.
											</td>
										</tr>
									) : (
										(amendmentsSection?.pending_amendments || []).map((amendment) => (
											<tr key={amendment.id}>
												<td>{amendment.is_time_and_materials ? "T&M" : "Fixed Fee"}</td>
												<td>{formatCurrency(amendment.amount)}</td>
												<td>{formatTimestamp(amendment.submitted_on)}</td>
												<td>{renderAmendmentScopeBadge(amendment)}</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</div>
		</div>
	);

	const documentStatusLabels = { pending: "Pending", expired: "Expired", signed: "Signed" };

	const renderDocumentsTable = (docs) => (
		<table className="projectViewerTable">
			<thead>
				<tr>
					<th>Name</th>
					<th>Status</th>
					<th>Date</th>
					<th />
				</tr>
			</thead>
			<tbody>
				{docs.length === 0 ? (
					<tr>
						<td colSpan="4" className="projectViewerEmptyCell">
							No documents found.
						</td>
					</tr>
				) : (
					docs.map((doc) => (
						<tr key={doc.id}>
							<td>{doc.name || notApplicable}</td>
							<td>
								<span className={`projectViewerDocStatusBadge ${doc.status || "pending"}`}>
									{documentStatusLabels[doc.status] || "Pending"}
								</span>
							</td>
							<td>{formatTimestamp(doc.date)}</td>
							<td>
								<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
									<Tooltip title="View document">
										<IconButton size="small" style={{ padding: 4 }} onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}>
											<VisibilityIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									{doc.url && (
										<a href={doc.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center" }}>
											<LaunchIcon fontSize="small" />
										</a>
									)}
								</span>
							</td>
						</tr>
					))
				)}
			</tbody>
		</table>
	);

	const renderDocumentsTab = () => {
		const allDocuments = documentsSection?.documents || [];
		const proposalDocuments = allDocuments.filter((doc) => doc.type !== "amendment");
		const amendmentDocuments = allDocuments.filter((doc) => doc.type === "amendment");

		return (
			<div className="projectViewerTabPanel" style={{ gap: 16 }}>
				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader projectViewerCommHeader">
						<div>
							<h3>Proposal &amp; Amendment Documents</h3>
							<p>Synced from HubSpot</p>
						</div>
						<div className="projectViewerMiniTabs" role="tablist">
							<button
								type="button"
								role="tab"
								aria-selected={documentsSubTab === "proposals"}
								className={`projectViewerMiniTab${documentsSubTab === "proposals" ? " active" : ""}`}
								onClick={() => setDocumentsSubTab("proposals")}
							>
								Proposals
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={documentsSubTab === "amendments"}
								className={`projectViewerMiniTab${documentsSubTab === "amendments" ? " active" : ""}`}
								onClick={() => setDocumentsSubTab("amendments")}
							>
								Amendments
							</button>
						</div>
					</div>
					<div className="projectViewerPaperBody projectViewerCompactTableBody">
						{sectionStatus["documents"]?.loading ? (
							renderInlineLoadingNotice("Loading documents...")
						) : sectionStatus["documents"]?.error ? (
							renderSectionErrorPanel(sectionStatus["documents"].error)
						) : (
							<>
								<div style={{ display: documentsSubTab === "proposals" ? undefined : "none" }}>
									{renderDocumentsTable(proposalDocuments)}
								</div>
								<div style={{ display: documentsSubTab === "amendments" ? undefined : "none" }}>
									{renderDocumentsTable(amendmentDocuments)}
								</div>
							</>
						)}
					</div>
				</div>
				{renderWeeklyReportsSection()}
			</div>
		);
	};

	const renderBudgetTab = () => (
		<BudgetEditorTab
			key={selectedBqeId || selectedPhaseId}
			bqeBudgetUrl={bqeBudgetUrl}
			budgetsSection={budgetsSection}
			timeEntriesSection={timeEntriesSection}
			expenseEntriesSection={expenseEntriesSection}
			atAGlanceSection={atAGlanceSection}
			hasBqeId={!!phaseReportModel?.phase_bqe_id}
			phaseBqeId={phaseReportModel?.phase_bqe_id || ""}
			loading={!!(sectionStatus["budgets"]?.loading || sectionStatus["time_entries"]?.loading || sectionStatus["expense_entries"]?.loading || sectionStatus["at_a_glance"]?.loading)}
			error={sectionStatus["budgets"]?.error || sectionStatus["time_entries"]?.error || sectionStatus["expense_entries"]?.error || sectionStatus["at_a_glance"]?.error || ""}
			phaseRecordId={selectedPhaseId}
			phaseName={selectedPhaseName}
		/>
	);

	const renderTimeEntriesTab = () => (
		<div className="projectViewerTabPanel">
			{phaseReportModel !== null && !phaseReportModel.phase_bqe_id && !sectionStatus["time_entries"]?.loading && renderMissingInfoNotice(
				"Time entry information is incomplete for this phase."
			)}
			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<h3>Time Entries</h3>
						<p>Up To Date</p>
					</div>
					<a
						className={`projectViewerQuickLinkItem projectViewerInlineDealLink${!bqeUrl ? " disabled" : ""}`}
						href={bqeUrl || undefined}
						target="_blank"
						rel="noreferrer"
					>
						<div className="projectViewerIntegrationBadge bqe">BQ</div>
						<div className="projectViewerQuickLinkTitle">BQE Budget</div>
						<LaunchIcon fontSize="small" />
					</a>
				</div>
				<div className="projectViewerPaperBody">
					{sectionStatus["time_entries"]?.loading ? (
						renderInlineLoadingNotice("Loading time entry stats...")
					) : sectionStatus["time_entries"]?.error ? (
						renderSectionErrorPanel(sectionStatus["time_entries"].error)
					) : (
						<div className="projectViewerKeyValueGrid">
							<div className="projectViewerKey">All Time Total Hours Worked</div>
							<div className="projectViewerValue">
								{timeEntriesSection?.all_time_total_hours != null
									? formatNumber(timeEntriesSection.all_time_total_hours)
									: notApplicable}
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="projectViewerPaper projectViewerBudgetTablesGrid">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<h3>Time Entry Table</h3>
						<p>Up to date</p>
					</div>
					<div style={{ display: "flex", gap: 8 }}>
						<Button
							size="small"
							variant="outlined"
							startIcon={<PictureAsPdfIcon />}
							disabled={sectionStatus["time_entries"]?.loading || filteredTimeEntries.length === 0}
							onClick={handleExportTimeEntriesPdf}
						>
							Export PDF
						</Button>
						<Button
							size="small"
							variant="outlined"
							startIcon={<BoltIcon />}
							disabled={!phaseReportModel?.phase_bqe_id}
							onClick={() => setQuickAddOpen(true)}
						>
							Add Time
						</Button>
					</div>
				</div>
				<div className="projectViewerPaperBody projectViewerCompactTableBody">
					{sectionStatus["time_entries"]?.loading ? (
						renderInlineLoadingNotice("Loading time entries...")
					) : sectionStatus["time_entries"]?.error ? (
						renderSectionErrorPanel(sectionStatus["time_entries"].error)
					) : (
						<>
							<div className="projectViewerTableFilters">
								<select
									className="projectViewerFilterSelect"
									value={timeEntriesEmployeeFilter}
									onChange={(e) => setTimeEntriesEmployeeFilter(e.target.value)}
								>
									<option value="All">All Employees</option>
									{timeEntryEmployees.map((name) => (
										<option key={name} value={name}>{name}</option>
									))}
								</select>
								<div className="projectViewerMiniTabs" role="group" aria-label="Billable filter">
									{[["all", "All"], ["billable", "Billable"], ["nonBillable", "Non-Billable"]].map(([val, label]) => (
										<button
											key={val}
											type="button"
											className={`projectViewerMiniTab${timeEntriesBillableFilter === val ? " active" : ""}`}
											onClick={() => setTimeEntriesBillableFilter(val)}
										>
											{label}
										</button>
									))}
								</div>
								<label className="projectViewerFilterDateLabel">
									From
									<input
										type="date"
										className="projectViewerFilterDate"
										value={timeEntriesDateFrom}
										onChange={(e) => setTimeEntriesDateFrom(e.target.value)}
									/>
								</label>
								<label className="projectViewerFilterDateLabel">
									To
									<input
										type="date"
										className="projectViewerFilterDate"
										value={timeEntriesDateTo}
										onChange={(e) => setTimeEntriesDateTo(e.target.value)}
									/>
								</label>
							</div>
							<table className="projectViewerTable">
								<colgroup>
									<col className="projectViewerColDate" />
									<col className="projectViewerColEmployee" />
									<col className="projectViewerColHours" />
									<col style={{ width: "80px" }} />
									<col />
								</colgroup>
								<thead>
									<tr>
										<th>Date</th>
										<th>Employee</th>
										<th>Hours</th>
										<th>Billable</th>
										<th>Memo</th>
									</tr>
								</thead>
								<tbody>
									{filteredTimeEntries.length === 0 ? (
										<tr>
											<td colSpan="5" className="projectViewerEmptyCell">
												No time entries found.
											</td>
										</tr>
									) : (
										filteredTimeEntries.map((entry, index) => (
											<tr
												key={entry.id ? entry.id : `${entry.date}|${entry.employee}|${entry.hours}|${entry.memo}|${index}`}
											>
												<td className="projectViewerCellNoWrap">{formatDate(entry.date)}</td>
												<td className="projectViewerCellNoWrap">{entry.employee}</td>
												<td>{entry.hours}</td>
												<td>
													<span className={`projectViewerBillableBadge${entry.billable ? " billable" : " nonBillable"}`}>
														{entry.billable ? "Yes" : "No"}
													</span>
												</td>
												<td>{entry.memo}</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</>
					)}
				</div>
			</div>
		</div>
	);

	const renderInvoicesTab = () => {
		const invoiceRows =
			invoiceListTab === "outstanding"
				? unpaidInvoices
				: invoiceListTab === "draft"
					? draftInvoices
					: paidInvoices;
		const emptyMessage =
			invoiceListTab === "outstanding"
				? "No outstanding unpaid invoices."
				: invoiceListTab === "draft"
					? "No draft invoices."
					: "No paid invoices.";

		return (
			<div className="projectViewerTabPanel" style={{ gap: 16 }}>
				{phaseReportModel !== null && !phaseReportModel.phase_bqe_id && !sectionStatus["invoices"]?.loading && renderMissingInfoNotice(
					"Invoice information is incomplete for this phase."
				)}
				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
						<div>
							<h3>Invoices</h3>
							<p>Up to date</p>
						</div>
						<a
							className={`projectViewerQuickLinkItem projectViewerInlineDealLink${!bqeTransactionsUrl ? " disabled" : ""}`}
							href={bqeTransactionsUrl || undefined}
							target="_blank"
							rel="noreferrer"
						>
							<div className="projectViewerIntegrationBadge bqe">BQ</div>
							<div className="projectViewerQuickLinkTitle">BQE Transactions</div>
							<LaunchIcon fontSize="small" />
						</a>
					</div>
					<div className="projectViewerPaperBody">
						{sectionStatus["invoices"]?.loading ? (
							renderInlineLoadingNotice("Loading invoice data...")
						) : sectionStatus["invoices"]?.error ? (
							renderSectionErrorPanel(sectionStatus["invoices"].error)
						) : (
							<div className="projectViewerKeyValueGrid">
								<div className="projectViewerKey">Outstanding Unpaid</div>
								<div className="projectViewerValue">
									{invoicesSection?.invoices
										? `${unpaidInvoices.length} — ${formatCurrency(unpaidInvoices.reduce((sum, inv) => sum + parseFloat(inv.balance || 0), 0))}`
										: notApplicable}
								</div>
								<div className="projectViewerKey">Draft</div>
								<div className="projectViewerValue">
									{invoicesSection?.invoices ? draftInvoices.length : notApplicable}{draftInvoices.length > 0 ? ` — ${formatCurrency(draftInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0))}` : " — $0.00"}
								</div>
								<div className="projectViewerKey">Paid</div>
								<div className="projectViewerValue">
									{paidInvoices.length}{paidInvoices.length > 0 ? ` — ${formatCurrency(paidInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0))}` : " — $0.00"}
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="projectViewerPaper">
					<div className="projectViewerPaperHeader projectViewerCommHeader">
						<div>
							<h3>Invoice List</h3>
							<p>Up to date</p>
						</div>
						<div className="projectViewerMiniTabs" role="tablist">
							<button
								type="button"
								role="tab"
								aria-selected={invoiceListTab === "outstanding"}
								className={`projectViewerMiniTab${invoiceListTab === "outstanding" ? " active" : ""}`}
								onClick={() => setInvoiceListTab("outstanding")}
							>
								Outstanding
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={invoiceListTab === "draft"}
								className={`projectViewerMiniTab${invoiceListTab === "draft" ? " active" : ""}`}
								onClick={() => setInvoiceListTab("draft")}
							>
								Draft
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={invoiceListTab === "paid"}
								className={`projectViewerMiniTab${invoiceListTab === "paid" ? " active" : ""}`}
								onClick={() => setInvoiceListTab("paid")}
							>
								Paid
							</button>
						</div>
					</div>
					<div className="projectViewerPaperBody projectViewerCompactTableBody">
						{sectionStatus["invoices"]?.loading ? (
							renderInlineLoadingNotice("Loading invoices...")
						) : sectionStatus["invoices"]?.error ? (
							renderSectionErrorPanel(sectionStatus["invoices"].error)
						) : (
							<table className="projectViewerTable">
								<thead>
									<tr>
										<th>Invoice #</th>
										<th>Invoice Date</th>
										<th>Due Date</th>
										<th>Amount</th>
										<th>Balance</th>
										{invoiceListTab === "outstanding" && <th>Overdue?</th>}
									</tr>
								</thead>
								<tbody>
									{invoiceRows.length === 0 ? (
										<tr>
											<td colSpan={invoiceListTab === "outstanding" ? 6 : 5} className="projectViewerEmptyCell">
												{emptyMessage}
											</td>
										</tr>
									) : (
										invoiceRows.map((invoice) => (
											<tr key={invoice.id || invoice.invoice_number}>
												<td>
													{invoice.id ? (
														<a
															href={`https://wap1-01.bqecore.com/webapp/InvoiceReview/Detail/${invoice.id}`}
															target="_blank"
															rel="noreferrer"
															className="projectViewerTableLink"
														>
															{invoice.invoice_number}
														</a>
													) : (
														invoice.invoice_number
													)}
													{invoice.is_late_fee_invoice && (
														<span style={{ marginLeft: 6, fontSize: "0.85em", opacity: 0.7 }}>(Late fee)</span>
													)}
												</td>
												<td>{formatDate(invoice.invoice_date)}</td>
												<td>{formatDate(invoice.due_date)}</td>
												<td>{invoice.amount != null ? formatCurrency(invoice.amount) : notApplicable}</td>
												<td>{invoice.balance != null ? formatCurrency(invoice.balance) : notApplicable}</td>
												{invoiceListTab === "outstanding" && (
													<td>{invoice.is_overdue ? "Yes" : "No"}</td>
												)}
											</tr>
										))
									)}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderCompanyCamTab = () => (
		<div className="projectViewerTabPanel">
			{atAGlanceLoaded && !companyCamUrl && renderMissingInfoNotice(
				"Photo information is incomplete for this phase."
			)}
			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<h3>CompanyCam</h3>
						<p>Up To Date</p>
					</div>
					<a
						className={`projectViewerQuickLinkItem projectViewerInlineDealLink${!companyCamUrl ? " disabled" : ""}`}
						href={companyCamUrl || undefined}
						target="_blank"
						rel="noreferrer"
					>
						<div className="projectViewerIntegrationBadge ccam">CC</div>
						<div className="projectViewerQuickLinkTitle">CompanyCam</div>
						<LaunchIcon fontSize="small" />
					</a>
				</div>
				<div className="projectViewerPaperBody">
					{sectionStatus["companycam"]?.loading ? (
						renderInlineLoadingNotice("Loading CompanyCam data...")
					) : sectionStatus["companycam"]?.error ? (
						renderSectionErrorPanel(sectionStatus["companycam"].error)
					) : (
						<div className="projectViewerKeyValueGrid">
							<div className="projectViewerKey">All-time Photos Taken</div>
							<div className="projectViewerValue">
								{companyCamSection?.all_time_photo_count ?? notApplicable}
							</div>
							<div className="projectViewerKey">
								Photos Taken{reportingPeriodDisplay ? ` (${reportingPeriodDisplay})` : " This Period"}
							</div>
							<div className="projectViewerValue">
								{companyCamSection?.weekly_photo_count ?? notApplicable}
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="projectViewerPaper projectViewerBudgetTablesGrid">
				<div className="projectViewerPaperHeader">
					<div>
						<h3>Photos by Employee (All Time)</h3>
						<p>Up To Date</p>
					</div>
				</div>
				<div className="projectViewerPaperBody projectViewerCompactTableBody">
					{sectionStatus["companycam"]?.loading ? (
						renderInlineLoadingNotice("Loading photo data...")
					) : sectionStatus["companycam"]?.error ? (
						renderSectionErrorPanel(sectionStatus["companycam"].error)
					) : (
						<>
							<div className="projectViewerMiniTabs" style={{ marginBottom: "14px" }}>
								<button
									type="button"
									className={`projectViewerMiniTab${photosView === "all_time" ? " active" : ""}`}
									onClick={() => setPhotosView("all_time")}
								>
									All Time
								</button>
								<button
									type="button"
									className={`projectViewerMiniTab${photosView === "weekly" ? " active" : ""}`}
									onClick={() => setPhotosView("weekly")}
								>
									{reportingPeriodDisplay || "This Period"}
								</button>
							</div>
							<table className="projectViewerTable">
								<thead>
									<tr>
										<th
											className="projectViewerSortableHeader"
											onClick={() => handlePhotosSort("employee_name")}
										>
											Employee Name
											{photosSortKey === "employee_name" && (
												<span className="projectViewerSortIndicator">
													{photosSortDir === "asc" ? " ↑" : " ↓"}
												</span>
											)}
										</th>
										<th
											className="projectViewerSortableHeader"
											onClick={() => handlePhotosSort("photo_count")}
										>
											Photo Count
											{photosSortKey === "photo_count" && (
												<span className="projectViewerSortIndicator">
													{photosSortDir === "asc" ? " ↑" : " ↓"}
												</span>
											)}
										</th>
									</tr>
								</thead>
								<tbody>
									{sortedPhotos.length === 0 ? (
										<tr>
											<td colSpan="2" className="projectViewerEmptyCell">
												No photos found.
											</td>
										</tr>
									) : (
										sortedPhotos.map((row) => (
											<tr key={row.employee_name}>
												<td>{row.employee_name}</td>
												<td>{row.photo_count}</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</>
					)}
				</div>
			</div>
		</div>
	);

	const renderDropboxTab = () => (
		<div className="projectViewerTabPanel" style={{ gap: 16 }}>
			{atAGlanceLoaded && !dropboxPath && renderMissingInfoNotice(
				"File information is incomplete for this phase."
			)}
			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<h3>Dropbox</h3>
						<p>Up To Date</p>
					</div>
					<a
						className={`projectViewerQuickLinkItem projectViewerInlineDealLink${!dropboxPath ? " disabled" : ""}`}
						href={dropboxPath ? dropboxFolderUrl : undefined}
						target="_blank"
						rel="noreferrer"
					>
						<div className="projectViewerIntegrationBadge dropbox">DB</div>
						<div className="projectViewerQuickLinkTitle">Dropbox Folder</div>
						<LaunchIcon fontSize="small" />
					</a>
				</div>
				<div className="projectViewerPaperBody">
					{!dropboxMetricsReady || sectionStatus["dropbox"]?.loading ? (
						renderInlineLoadingNotice("Loading Dropbox data...")
					) : sectionStatus["dropbox"]?.error ? (
						renderSectionErrorPanel(sectionStatus["dropbox"].error)
					) : (
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 32px" }}>
							<div>
								<div style={{ fontWeight: 700, fontSize: 12, color: "#7a8190", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Phase Folder</div>
								<div className="projectViewerKeyValueGrid">
									<div className="projectViewerKey">Path</div>
									<div className="projectViewerValue" style={{ fontSize: 12, wordBreak: "break-all" }}>
										{dropboxSection?.dropbox_path || selectedPhase?.dropboxPath || notApplicable}
									</div>
									<div className="projectViewerKey">All-time File Changes</div>
									<div className="projectViewerValue">{dropboxSection?.all_time_activity_count ?? notApplicable}</div>
									<div className="projectViewerKey">
										File Changes{reportingPeriodDisplay ? ` (${reportingPeriodDisplay})` : " This Period"}
									</div>
									<div className="projectViewerValue">{dropboxSection?.activity_count ?? notApplicable}</div>
								</div>
							</div>
							<div>
								<div style={{ fontWeight: 700, fontSize: 12, color: "#7a8190", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>General Folder</div>
								<div className="projectViewerKeyValueGrid">
									<div className="projectViewerKey">Path</div>
									<div className="projectViewerValue" style={{ fontSize: 12, wordBreak: "break-all" }}>
										{(() => {
											const base = dropboxSection?.dropbox_path || selectedPhase?.dropboxPath || "";
											return base ? base.replace(/\/[^/]+$/, "") + "/1. General" : notApplicable;
										})()}
									</div>
									<div className="projectViewerKey">All-time File Changes</div>
									<div className="projectViewerValue">{dropboxSection?.general_all_time_activity_count ?? notApplicable}</div>
									<div className="projectViewerKey">
										File Changes{reportingPeriodDisplay ? ` (${reportingPeriodDisplay})` : " This Period"}
									</div>
									<div className="projectViewerValue">{dropboxSection?.general_activity_count ?? notApplicable}</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCommHeader">
					<div>
						<h3>Dropbox Folder Contents</h3>
						<p>Up To Date</p>
					</div>
					<div className="projectViewerMiniTabs" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={dropboxFolderTab === "phase"}
							className={`projectViewerMiniTab${dropboxFolderTab === "phase" ? " active" : ""}`}
							onClick={() => setDropboxFolderTab("phase")}
						>
							Phase Folder
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={dropboxFolderTab === "general"}
							className={`projectViewerMiniTab${dropboxFolderTab === "general" ? " active" : ""}`}
							onClick={() => setDropboxFolderTab("general")}
						>
							General Folder
						</button>
					</div>
				</div>
				<div className="projectViewerPaperBody projectViewerCompactTableBody">
					<div style={{ display: dropboxFolderTab === "phase" ? undefined : "none" }}>
						<DropboxFolderView
							folderPath={dropboxPath}
							reportingWindowStart={atAGlanceSection?.reporting_window?.period_start}
							reportingWindowEnd={atAGlanceSection?.reporting_window?.period_end}
							reportingPeriodDisplay={reportingPeriodDisplay}
							filterState={dropboxFilterState}
							onFilterChange={setDropboxFilterState}
						/>
					</div>
					<div style={{ display: dropboxFolderTab === "general" ? undefined : "none" }}>
						<DropboxFolderView
							folderPath={dropboxGeneralPath}
							reportingWindowStart={atAGlanceSection?.reporting_window?.period_start}
							reportingWindowEnd={atAGlanceSection?.reporting_window?.period_end}
							reportingPeriodDisplay={reportingPeriodDisplay}
							filterState={dropboxFilterState}
							onFilterChange={setDropboxFilterState}
						/>
					</div>
				</div>
			</div>
		</div>
	);

	const renderWeeklyReportsSection = () => (
		<div className="projectViewerPaper">
			<div className="projectViewerPaperHeader">
				<div>
					<h3>Weekly Reports</h3>
					<p>Past reports available for download by report period.</p>
				</div>
			</div>
			<div className="projectViewerPaperBody projectViewerReportsBody">
				{sectionStatus["weekly_reports"]?.loading ? (
					renderInlineLoadingNotice("Loading weekly reports...")
				) : sectionStatus["weekly_reports"]?.error ? (
					renderSectionErrorPanel(sectionStatus["weekly_reports"].error)
				) : weeklyReports.length === 0 ? (
					<div className="projectViewerEmptyStateMessage">
						No weekly reports available.
					</div>
				) : (
					[...weeklyReports].sort((a, b) => {
						const da = a.report_period_start || a.title || "";
						const db = b.report_period_start || b.title || "";
						return db.localeCompare(da);
					}).map((report) => {
						const reportKey = report.title || report.report_period_start;
						const isExpanded = !!expandedWeeklyReports[reportKey];
						return (
							<div
								className="projectViewerReportCard"
								key={reportKey}
							>
								<div className="projectViewerReportHeader">
									<div>
										<h4>{report.report_period}</h4>
									</div>
									<div className="projectViewerReportActions">
										<button
											type="button"
											className={`projectViewerCollapseToggle projectViewerInlineCollapse${isExpanded ? " open" : ""}`}
											onClick={() =>
												setExpandedWeeklyReports((prev) => ({
													...prev,
													[reportKey]: !prev[reportKey],
												}))
											}
										>
											<span className="projectViewerCollapseLabel">
												{isExpanded ? "Hide" : "Show"} Summary
											</span>
											<ExpandMoreIcon fontSize="inherit" />
										</button>
										<button
											type="button"
											className="projectViewerQuickLinkItem projectViewerInlineDealLink projectViewerReportDownloadLink"
											onClick={() => handleDownloadWeeklyReport(report.record_id)}
											disabled={downloadingReportId === report.record_id}
										>
											<div className="projectViewerIntegrationBadge bqe">
												PDF
											</div>
											<div className="projectViewerQuickLinkTitle">
												{downloadingReportId === report.record_id ? "Preparing…" : "Download PDF"}
											</div>
											<LaunchIcon fontSize="small" />
										</button>
										{reportDownloadError === report.record_id && (
											<span className="projectViewerStatusError">Download unavailable — try again.</span>
										)}
									</div>
								</div>
								{isExpanded && (
									<div className="projectViewerReportSummary">
										{report.weekly_summary
											? report.weekly_summary
											: <span className="projectViewerEmptyNotice">No summary available for this report.</span>
										}
									</div>
								)}
							</div>
						);
					})
				)}
			</div>
		</div>
	);

	const handleSaveSchedule = async (startDate, durationWeeks, endDate) => {
		const bqeId = selectedPhaseRecord?.["BQE Id"];
		if (!bqeId) throw new Error("No BQE phase ID available.");

		await putAPICall(instance, accounts[0], "/api/bqe/phase/schedule", {
			bqePhaseId: bqeId,
			...(startDate !== null && startDate !== undefined && { startDate }),
			...(durationWeeks !== null && durationWeeks !== undefined && { durationWeeks }),
			...(endDate !== null && endDate !== undefined && { endDate }),
		});

		// Reflect the just-saved values immediately — we already know exactly what was
		// written (this only runs once the PUT above has confirmed success), so there's
		// no need to re-fetch. Schedule reads elsewhere now come from NocoDB, which won't
		// have this edit until the next sync — re-fetching here would show the stale value.
		setPhaseReportModel((current) => mergePhaseReportSection(current, "at_a_glance", {
			estimated_project_duration: {
				start_date: startDate || "",
				duration_weeks: durationWeeks ?? null,
				end_date: endDate || "",
				estimated_end_date: endDate || "",
				display_text: durationWeeks !== null && durationWeeks !== undefined ? `${durationWeeks} weeks` : "",
			},
		}));
	};

	const handleSaveOnHoldSchedule = async (startDate, durationWeeks, endDate) => {
		const bqeId = selectedPhaseRecord?.["BQE Id"];
		if (!bqeId) throw new Error("No BQE phase ID available.");

		// Unlike handleSaveSchedule, explicit null here means "clear this field" (the
		// user unchecked "Add on hold period") rather than "leave untouched" — so null
		// values are sent through, not filtered out.
		await putAPICall(instance, accounts[0], "/api/bqe/phase/schedule", {
			bqePhaseId: bqeId,
			...(startDate !== undefined && { onHoldStartDate: startDate }),
			...(durationWeeks !== undefined && { onHoldDurationWeeks: durationWeeks }),
			...(endDate !== undefined && { onHoldEndDate: endDate }),
		});

		// Same rationale as handleSaveSchedule above — reflect the confirmed write
		// immediately rather than re-fetching a NocoDB mirror that hasn't caught up yet.
		setPhaseReportModel((current) => mergePhaseReportSection(current, "at_a_glance", {
			on_hold_period: {
				start_date: startDate || "",
				duration_weeks: durationWeeks ?? null,
				end_date: endDate || "",
				estimated_end_date: endDate || "",
				display_text: durationWeeks !== null && durationWeeks !== undefined ? `${durationWeeks} weeks` : "",
			},
		}));
	};

	const handleSaveStatus = async (statusKey) => {
		const bqeId = selectedPhaseRecord?.["BQE Id"];
		if (!bqeId) throw new Error("No BQE phase ID available.");

		const res = await putAPICall(instance, accounts[0], "/api/bqe/phase/status", {
			bqePhaseId: bqeId,
			status: statusKey,
		});

		// Reflect the confirmed write immediately — same rationale as handleSaveSchedule:
		// the NocoDB-backed sections won't see this until the next sync.
		setPhaseReportModel((current) => mergePhaseReportSection(current, "phase", {
			"BQE Status": res?.data?.status || statusKey,
		}));

		// Notify on every phase completion, regardless of sibling phases — allPhasesCompleted
		// only gates the HubSpot deal closeout (handled server-side), not this notification.
		// isLastPhase carries the real value through so the email copy can say whether the
		// deal was actually closed out or is still active. Best-effort: an email hiccup
		// shouldn't undo/block the completion that already succeeded, so failures are logged
		// and swallowed, not thrown. The deal owner's name/email is resolved server-side.
		if (statusKey === "completed" && res?.data?.hubspotDealId) {
			try {
				await postAPICall(instance, accounts[0], "/api/outlook/project/emailFinished", {
					hubspotDealId: res.data.hubspotDealId,
					propertyName: selectedPropertyName,
					projectName: selectedProjectName,
					phaseName: selectedPhaseName,
					hubspotLink: hubspotDealUrl,
					bqeLink: bqeUrl,
					companyCamLink: companyCamUrl,
					dropboxLink: dropboxPath ? dropboxFolderUrl : "",
					isLastPhase: !!res?.data?.allPhasesCompleted,
					completedBy: employeeID,
				});
			} catch (err) {
				console.error("[ProjectViewer] failed to send project-finished notification", err);
			}
		}
	};

	const handleLogSaveFailure = async (message) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID,
				message: `Project Viewer status/schedule save failed: ${message}`,
				success: false,
				tool_name: "Project Viewer",
			});
		} catch (err) {
			console.error("[ProjectViewer] failed to write automation log", err);
		}
	};

	const renderStatusScheduleDialog = () => {
		const onHoldPeriod = atAGlanceSection?.on_hold_period;
		return (
			<StatusScheduleDialog
				open={statusDialogOpen}
				onClose={() => setStatusDialogOpen(false)}
				projectName={[selectedPropertyName, selectedProjectName, selectedPhaseName].filter(Boolean).join(": ")}
				currentStatus={String(selectedPhaseRecord?.["BQE Status"] || "").trim().toLowerCase()}
				rawStart={schedDur.start_date || selectedPhaseRecord?.["CF-Start Date"] || ""}
				rawEndDate={schedDur.estimated_end_date || schedDur.end_date || selectedPhaseRecord?.["CF-End Date"] || ""}
				onHoldRawStart={onHoldPeriod?.start_date || ""}
				onHoldRawEndDate={onHoldPeriod?.estimated_end_date || onHoldPeriod?.end_date || ""}
				onSaveStatus={handleSaveStatus}
				onSaveSchedule={handleSaveSchedule}
				onSaveOnHoldSchedule={handleSaveOnHoldSchedule}
				onLogFailure={handleLogSaveFailure}
				onCompleted={() => handlePhaseSelect("", "")}
			/>
		);
	};

	const renderScheduleTab = () => (
		<ScheduleTab
			key={selectedBqeId || selectedPhaseId}
			atAGlanceSection={atAGlanceSection}
			budgetsSection={budgetsSection}
			timeEntriesSection={timeEntriesSection}
			sectionStatus={sectionStatus}
			phaseRecord={selectedPhaseRecord}
			timeOffByName={scheduleTimeOff}
			holidays={scheduleHolidays}
			holidaysLoaded={scheduleHolidaysLoaded}
			onSaveSchedule={handleSaveSchedule}
			onHoldSection={atAGlanceSection?.on_hold_period}
			onSaveOnHoldSchedule={handleSaveOnHoldSchedule}
		/>
	);

	const renderDesignTab = () => (
		<DesignTab
			key={selectedBqeId}
			bqePhaseId={selectedBqeId}
			phaseName={[selectedPropertyName, selectedProjectName, selectedPhaseName].filter(Boolean).join(": ")}
			hasDesignAccess={!!props.hasDesignAccess}
			refreshCount={refreshCount}
		/>
	);

	const renderTabPanel = () => {
		switch (activeTab) {
			case "design":
				return renderDesignTab();
			case "amendments":
				return renderAmendmentsTab();
			case "documents":
				return renderDocumentsTab();
			case "contacts":
				return renderContactsTab();
			case "budget":
				return renderBudgetTab();
			case "timeEntries":
				return renderTimeEntriesTab();
			case "invoices":
				return renderInvoicesTab();
			case "dropbox":
				return renderDropboxTab();
			case "companyCam":
				return renderCompanyCamTab();
			case "atAGlance":
			default:
				return renderAtAGlanceTab();
		}
	};

	return (
		<div className="ProjectViewer">
			<div className="projectViewerPage">
				<div
					className={`projectViewerWorkspace${
						browserPaneCollapsed ? " browserCollapsed" : ""
					}`}
				>
					<div className="projectViewerBrowserPane projectViewerPaper">
						<div className="projectViewerPaperHeader projectViewerBrowserHeader">
							<div>
								<h3>Project Viewer</h3>
							</div>
							{/* A span, not a div — the collapse CSS hides direct `div`
							children of this header so these buttons stay usable while
							collapsed; a div wrapper here would hide them too. */}
							<span className="projectViewerCardHeaderActions">
								{!browserPaneCollapsed && props.isAdmin && (
									<button
										type="button"
										className="projectViewerBrowserToggle"
										aria-label="Add project or phase"
										title="Add project or phase"
										onClick={handleAddMenuOpen}
									>
										<AddIcon fontSize="inherit" />
									</button>
								)}
								<Menu
									anchorEl={addMenuAnchor}
									open={Boolean(addMenuAnchor)}
									onClose={handleAddMenuClose}
								>
									<MenuItem
										onClick={() => handleAddOptionSelect("project")}
										disabled={addDialogDataLoading && !addDialogDataLoaded}
									>
										Add Project
									</MenuItem>
									<MenuItem
										onClick={() => handleAddOptionSelect("phase")}
										disabled={addDialogDataLoading && !addDialogDataLoaded}
									>
										Add Phase
									</MenuItem>
								</Menu>
								<button
									type="button"
									className="projectViewerBrowserToggle"
									aria-label={
										browserPaneCollapsed
											? "Expand project viewer list"
											: "Collapse project viewer list"
									}
									onClick={() =>
										setBrowserPaneCollapsed((current) => !current)
									}
								>
									{browserPaneCollapsed ? (
										<ChevronRightIcon fontSize="inherit" />
									) : (
										<ChevronLeftIcon fontSize="inherit" />
									)}
								</button>
							</span>
						</div>
						<div
							className={`projectViewerPaperBody projectViewerBrowserBody${
								browserPaneCollapsed ? " collapsed" : ""
							}`}
						>
							<div className="projectViewerFilterStack">
								<input
									type="text"
									className="projectViewerFilterInput"
									placeholder="Search property, project, client, phase"
									value={searchTerm}
									onChange={(event) => setSearchTerm(event.target.value)}
								/>
								<select
									className="projectViewerFilterSelect"
									value={managerFilter}
									onChange={(event) => setManagerFilter(event.target.value)}
								>
									{managerOptions.map((option) => (
										<option key={option} value={option}>
											{option}
										</option>
									))}
								</select>
								<select
									className="projectViewerFilterSelect"
									value={stateFilter}
									onChange={(event) => setStateFilter(event.target.value)}
								>
									{stateOptions.map((option) => (
										<option key={option} value={option}>
											{option}
										</option>
									))}
								</select>
								<select
									className="projectViewerFilterSelect"
									value={statusFilter}
									onChange={(event) => setStatusFilter(event.target.value)}
								>
									{statusOptions.map((option) => (
										<option key={option} value={option}>
											{option}
										</option>
									))}
								</select>
							</div>
							{isBrowserLoading ? (
								renderBrowserSkeleton()
							) : (
								<div className="projectViewerProjectList">
									{browserError ? (
										<div className="projectViewerEmptyStateMessage">
											{browserError}
										</div>
									) : filteredPhases.length === 0 ? (
										<div className="projectViewerBrowserEmptyState">
											<InfoOutlinedIcon fontSize="inherit" />
											<p>No projects match your search.</p>
											<p>If you're looking for a recently created project, it may not be available here yet — check back next week.</p>
										</div>
									) : (
										groupedPhases.map((group) => (
											<div key={group.key} className="projectViewerProjectGroup">
												<div className="projectViewerProjectGroupHeader">
													{[group.propertyName, group.projectName].filter(Boolean).join(": ") || notApplicable}
												</div>
												{group.phases.map((phase) => (
													<button
														key={phase.recordId}
														type="button"
														className={`projectViewerProjectListItem${
															selectedBqeId === phase.bqeId ? " active" : ""
														}`}
														onClick={() =>
															handlePhaseSelect(phase.bqeId, phase.recordId)
														}
													>
														<div className="projectViewerProjectListTopRow">
															<div className="projectViewerProjectListTitleBlock">
																<div className="projectViewerProjectListTitle">
																	{phase.phaseName || phase.displayName || notApplicable}
																</div>
															</div>
															<div
																className={`projectViewerProjectStatusChip${
																	String(phase.status || "").trim().toLowerCase() === "on hold"
																		? " onHold"
																		: ""
																}`}
															>
																{phase.status}
															</div>
														</div>
														<div className="projectViewerProjectListMeta">
															<span>{phase.owner}</span>
															<span>{phase.client}</span>
														</div>
														<div className="projectViewerProjectListFooter">
															<span>{phase.location}</span>
														</div>
													</button>
												))}
											</div>
										))
									)}
								</div>
							)}
						</div>
					</div>

					<div className="projectViewerDetailPane">
						{selectedBqeId && selectedPhase ? (
							<>
								<div className="projectViewerHero projectViewerPaper">
									<div className="projectViewerHeroTop">
										<div className="projectViewerHeroTitleBlock">
											<div className="projectViewerEyebrowRow">
												<div className="projectViewerEyebrow">
													{selectedPhaseEyebrow}
												</div>
												<div className="projectViewerHeroNav">
													{renderHeroQuickLinkIcons()}
													<div className="projectViewerHeroNavDivider" />
													<button
														type="button"
														className="projectViewerNavArrow"
														disabled={!prevPhase}
														onClick={() => prevPhase && handlePhaseSelect(prevPhase.bqeId, prevPhase.recordId)}
														aria-label="Previous project"
													>
														<ChevronLeftIcon fontSize="inherit" />
													</button>
													<span className="projectViewerNavPosition">
														{currentFilteredIndex + 1} / {filteredPhases.length}
													</span>
													<button
														type="button"
														className="projectViewerNavArrow"
														disabled={!nextPhase}
														onClick={() => nextPhase && handlePhaseSelect(nextPhase.bqeId, nextPhase.recordId)}
														aria-label="Next project"
													>
														<ChevronRightIcon fontSize="inherit" />
													</button>
													<Tooltip arrow placement="top" title={isRefreshing ? "Refreshing…" : "Refresh this project's data"}>
														<span>
															<button
																type="button"
																className={`projectViewerNavArrow${isRefreshing ? " refreshing" : ""}`}
																disabled={isRefreshing}
																onClick={handleRefreshPhase}
																aria-label="Refresh project data"
															>
																<RefreshIcon fontSize="inherit" />
															</button>
														</span>
													</Tooltip>
													<div className="projectViewerHeroNavDivider" />
													<Tooltip arrow placement="top" title="Edit status & schedule">
														<span>
															<button
																type="button"
																className="projectViewerNavArrow"
																onClick={() => setStatusDialogOpen(true)}
																aria-label="Edit status and schedule"
															>
																<EditIcon fontSize="inherit" />
															</button>
														</span>
													</Tooltip>
													{props.isAdmin && (
														<>
															<Tooltip
																arrow
																placement="top"
																title={
																	isAmendmentPhase
																		? "Amendment phases are tracked through their amendment records, so they don't get Tools property, project or phase records. Open a regular phase on this project to edit those."
																		: addDialogDataLoaded
																		? "Edit or link property, project and phase Tools records (admin)"
																		: addDialogDataFailed
																		? "Couldn't load Tools records — refresh the page to retry"
																		: "Loading Tools records…"
																}
															>
																<span>
																	<button
																		type="button"
																		className="projectViewerNavArrow"
																		onClick={handleEditMenuOpen}
																		disabled={isAmendmentPhase || !addDialogDataLoaded}
																		aria-label="Edit project or phase details"
																	>
																		<SettingsIcon fontSize="inherit" />
																	</button>
																</span>
															</Tooltip>
														<Menu
															anchorEl={editMenuAnchor}
															open={Boolean(editMenuAnchor)}
															onClose={handleEditMenuClose}
														>
															{!editablePropertyRecord && (
																<MenuItem
																	onClick={handleLinkPropertySelect}
																	disabled={!selectedPropertyRecord?.["BQE Id"]}
																>
																	Link Property to Tools
																</MenuItem>
															)}
															{editableProjectRecord ? (
																<MenuItem onClick={() => handleEditOptionSelect("project")}>
																	Edit Project
																</MenuItem>
															) : (
																// A Tools project belongs to a Tools property, so the
																// property has to be linked first.
																<MenuItem
																	onClick={handleLinkProjectSelect}
																	disabled={!editablePropertyRecord || !selectedProjectRecord?.["BQE Id"]}
																>
																	{editablePropertyRecord
																		? "Link Project to Tools"
																		: "Link Project to Tools (link property first)"}
																</MenuItem>
															)}
															{editablePhaseRecord ? (
																<MenuItem onClick={() => handleEditOptionSelect("phase")}>
																	Edit Phase
																</MenuItem>
															) : (
																// A Tools phase belongs to a Tools project, so the project
																// has to be linked first.
																<MenuItem
																	onClick={handleLinkPhaseSelect}
																	disabled={!editableProjectRecord || !selectedPhaseRecord?.["BQE Id"]}
																>
																	{editableProjectRecord
																		? "Link Phase to Tools"
																		: "Link Phase to Tools (link project first)"}
																</MenuItem>
															)}
														</Menu>
														</>
													)}
												</div>
											</div>
											<h1>
												<span className="projectViewerStrong">
													{phaseReportModel?.local_property_id ? (
														<a
															href={`/properties/viewer?recordId=${encodeURIComponent(phaseReportModel.local_property_id)}&tab=overview`}
															target="_blank"
															rel="noopener noreferrer"
															className="projectViewerHeroPropertyLink"
															title="View property record"
														>
															{selectedPropertyName}
														</a>
													) : (
														selectedPropertyName
													)}
													:{" "}
													{selectedProjectName}:{" "}
													{selectedPhaseName}
												</span>
											</h1>
											<div className="projectViewerMetaRow">
												<div className="projectViewerMetaItem">
													<ApartmentIcon fontSize="inherit" />
													{phaseReportModel?.local_property_id ? (
														<a
															href={`/properties/viewer?recordId=${encodeURIComponent(phaseReportModel.local_property_id)}&tab=overview`}
															target="_blank"
															rel="noopener noreferrer"
															className="projectViewerMetaLink"
														>
															{selectedPropertyName}
														</a>
													) : (
														<strong>{selectedPropertyName}</strong>
													)}
													<span>Property</span>
												</div>
												<div className="projectViewerMetaItem">
													<PersonOutlineIcon fontSize="inherit" />
													<strong>
														{atAGlanceSection?.project_manager ||
															selectedPhase?.owner ||
															notApplicable}
													</strong>
													<span>Project Manager</span>
												</div>
												<div className="projectViewerMetaItem">
													<BusinessIcon fontSize="inherit" />
													<strong>
														{atAGlanceSection?.client ||
															selectedPhase?.client ||
															notApplicable}
													</strong>
													<span>Project Client</span>
												</div>
												<div className="projectViewerMetaItem">
													{String(
														selectedPhaseRecord?.["BQE Status"] ||
															atAGlanceSection?.phase_status ||
															selectedPhase?.status ||
															"",
													)
														.trim()
														.toLowerCase() === "on hold" ? (
														<AccessTimeIcon fontSize="inherit" />
													) : (
														<TaskAltIcon fontSize="inherit" />
													)}
													<strong>
														{selectedPhaseRecord?.["BQE Status"] ||
															atAGlanceSection?.phase_status ||
															selectedPhase?.status ||
															notApplicable}
													</strong>
													<span>Project Status</span>
												</div>
												<div className="projectViewerMetaItem">
													<TimelapseIcon fontSize="inherit" />
													<strong>{schedDur.duration_weeks != null ? `${schedDur.duration_weeks} wks` : notApplicable}</strong>
													<span>Duration</span>
												</div>
												<div className="projectViewerMetaItem">
													<EventAvailableIcon fontSize="inherit" />
													<strong>
														{selectedStartedDate} – {formatDate(schedDur.estimated_end_date || schedDur.end_date || selectedPhaseRecord?.["CF-End Date"])}
													</strong>
													<span>Schedule</span>
												</div>
											</div>
										</div>
									</div>

									{renderStatusScheduleDialog()}

									{phaseReportModel?.phase_bqe_id && (
										<ProjectQuickAdd
											open={quickAddOpen}
											onClose={closeQuickAdd}
											projectId={phaseReportModel.phase_bqe_id}
											onSent={() => {
												timeSentRef.current = true;
											}}
										/>
									)}

									<DocumentViewerDialog
										open={!!viewingDocument}
										onClose={() => setViewingDocument(null)}
										document={viewingDocument}
										instance={instance}
										accounts={accounts}
									/>

									{!allEagerLoaded && (
										<div className="projectViewerProgressBar">
											<div
												className="projectViewerProgressBarFill"
												style={{ width: `${loadProgress}%` }}
											/>
										</div>
									)}

									<div className="projectViewerTabs">
										{visibleTabs.map((tab) => (
											<button
												key={tab.id}
												type="button"
												className={`projectViewerTab${
													activeTab === tab.id ? " active" : ""
												}`}
												onClick={() => setActiveTab(tab.id)}
											>
												{tab.label}
												{tab.sectionKey && sectionStatus[tab.sectionKey]?.loading && (
													<span className="projectViewerTabLoadingDot" />
												)}
												{tab.sectionKey && sectionStatus[tab.sectionKey]?.error && !sectionStatus[tab.sectionKey]?.loading && (
													<span className="projectViewerTabErrorDot" title={sectionStatus[tab.sectionKey].error} />
												)}
											</button>
										))}
									</div>
								</div>

								{detailError ? (
									<div className="projectViewerPaper">
										<div className="projectViewerPaperBody">
											<div className="projectViewerEmptyStateMessage">
												{detailError}
											</div>
										</div>
									</div>
								) : (
									<>
										{activeTab !== "schedule" && renderTabPanel()}
										<div style={{ display: activeTab === "schedule" ? undefined : "none" }}>
											{renderScheduleTab()}
										</div>
									</>
								)}
							</>
						) : selectedBqeId && isBrowserLoading ? (
							renderDetailSkeleton()
						) : selectedBqeId ? (
							<div className="projectViewerPaper">
								<div className="projectViewerPaperBody">
									<div className="projectViewerEmptyStateMessage">
										{browserError ? (
											<>
												<strong>Couldn't load the project list.</strong>
												<div style={{ marginTop: 6 }}>{browserError}</div>
											</>
										) : (
											<>
												<strong>Project not found.</strong>
												<div style={{ marginTop: 6 }}>
													This project couldn't be found. It may have been marked Completed, removed, or the link may be out of date. If you believe this is an error, please contact IT.
												</div>
											</>
										)}
										<div style={{ marginTop: 16 }}>
											<Button
												variant="outlined"
												size="small"
												onClick={() => handlePhaseSelect("", "")}
											>
												Back to project list
											</Button>
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="projectViewerPaper">
								<div className="projectViewerPaperBody">
									<div className="projectViewerEmptyStateMessage">
										Select a project to view its details.
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
				<ProjectConsoleEditAddDialog
					open={addProjectOpen}
					editing={false}
					recordToEdit={EMPTY_RECORD_TO_EDIT}
					properties={addDialogProperties}
					employees={addDialogEmployees}
					buildingComponents={addDialogBuildingComponents}
					onClose={closeAddProjectAndRefresh}
					onCloseNoRefresh={closeAddProjectNoRefresh}
				/>
				<PhaseConsoleEditAddDialog
					open={addPhaseOpen}
					editing={false}
					recordToEdit={EMPTY_RECORD_TO_EDIT}
					properties={addDialogProperties}
					projects={addDialogProjects}
					employees={addDialogEmployees}
					phaseTypes={addDialogPhaseTypes}
					phaseNames={addDialogPhaseNames}
					onClose={closeAddPhaseAndRefresh}
					onCloseNoRefresh={closeAddPhaseNoRefresh}
				/>
				{props.isAdmin && editPhaseOpen && editablePhaseRecord && (
					<PhaseConsoleEditAddDialog
						open={editPhaseOpen}
						editing={true}
						recordToEdit={editablePhaseRecord}
						properties={addDialogProperties}
						projects={addDialogProjects}
						employees={addDialogEmployees}
						phaseTypes={addDialogPhaseTypes}
						phaseNames={addDialogPhaseNames}
						onClose={closeEditPhaseAndRefresh}
						onCloseNoRefresh={closeEditPhaseNoRefresh}
					/>
				)}
				{props.isAdmin && linkChooser && (
					<LinkToolsRecordDialog
						open={Boolean(linkChooser)}
						{...linkChooserProps()}
						onCreateNew={handleLinkCreateNew}
						onUseExisting={handleLinkUseExisting}
						onClose={() => setLinkChooser(null)}
					/>
				)}
				{props.isAdmin && relinkRecord?.type === "property" && (
					<PropertyConsoleEditAddDialog
						open
						editing={true}
						recordToEdit={relinkRecord.record}
						onClose={closeLinkPropertyAndRefresh}
						onCloseNoRefresh={closeLinkPropertyNoRefresh}
					/>
				)}
				{props.isAdmin && relinkRecord?.type === "project" && (
					<ProjectConsoleEditAddDialog
						open
						editing={true}
						recordToEdit={relinkRecord.record}
						properties={addDialogProperties}
						employees={addDialogEmployees}
						buildingComponents={addDialogBuildingComponents}
						onClose={closeLinkProjectAndRefresh}
						onCloseNoRefresh={closeLinkProjectNoRefresh}
					/>
				)}
				{props.isAdmin && relinkRecord?.type === "phase" && (
					<PhaseConsoleEditAddDialog
						open
						editing={true}
						recordToEdit={relinkRecord.record}
						properties={addDialogProperties}
						projects={addDialogProjects}
						employees={addDialogEmployees}
						phaseTypes={addDialogPhaseTypes}
						phaseNames={addDialogPhaseNames}
						onClose={closeLinkPhaseAndRefresh}
						onCloseNoRefresh={closeLinkPhaseNoRefresh}
					/>
				)}
				{props.isAdmin && linkPropertyPrefill && (
					<PropertyConsoleEditAddDialog
						open={Boolean(linkPropertyPrefill)}
						editing={false}
						recordToEdit={EMPTY_RECORD_TO_EDIT}
						prefillRecord={linkPropertyPrefill}
						onClose={closeLinkPropertyAndRefresh}
						onCloseNoRefresh={closeLinkPropertyNoRefresh}
					/>
				)}
				{props.isAdmin && linkProjectPrefill && (
					<ProjectConsoleEditAddDialog
						open={Boolean(linkProjectPrefill)}
						editing={false}
						recordToEdit={EMPTY_RECORD_TO_EDIT}
						prefillRecord={linkProjectPrefill}
						properties={addDialogProperties}
						employees={addDialogEmployees}
						buildingComponents={addDialogBuildingComponents}
						onClose={closeLinkProjectAndRefresh}
						onCloseNoRefresh={closeLinkProjectNoRefresh}
					/>
				)}
				{props.isAdmin && linkPhasePrefill && (
					<PhaseConsoleEditAddDialog
						open={Boolean(linkPhasePrefill)}
						editing={false}
						recordToEdit={EMPTY_RECORD_TO_EDIT}
						prefillRecord={linkPhasePrefill}
						properties={addDialogProperties}
						projects={addDialogProjects}
						employees={addDialogEmployees}
						phaseTypes={addDialogPhaseTypes}
						phaseNames={addDialogPhaseNames}
						onClose={closeLinkPhaseAndRefresh}
						onCloseNoRefresh={closeLinkPhaseNoRefresh}
					/>
				)}
				{props.isAdmin && editProjectOpen && editableProjectRecord && (
					<ProjectConsoleEditAddDialog
						open={editProjectOpen}
						editing={true}
						recordToEdit={editableProjectRecord}
						properties={addDialogProperties}
						employees={addDialogEmployees}
						buildingComponents={addDialogBuildingComponents}
						onClose={closeEditProjectAndRefresh}
						onCloseNoRefresh={closeEditProjectNoRefresh}
					/>
				)}
			</div>
		</div>
	);
};

export default ProjectViewer;
